import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent as ReactKeyboardEvent,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
} from 'react';
import { useQuery } from '@tanstack/react-query';
import * as L from 'leaflet';
import 'leaflet/dist/leaflet.css';

import { apiFetch } from '../../../auth/apiFetch.ts';
import { useFleetRoute } from '../../../router/useFleetRoute.ts';
import { useRobot } from '../../../hooks/useRobot.ts';
import { useTelemetry, type TelemetryReading } from '../../../hooks/useTelemetry.ts';
import {
  useIssueCommand,
  useRobotCommands,
  type Command,
  type CommandName,
  type CommandStatus,
} from '../../../hooks/useCommands.ts';
import { useCommandStream } from '../../../hooks/useCommandStream.ts';
import { useRobotRevisions } from '../../../hooks/useRevisions.ts';
import { useAuth } from '../../../hooks/useAuth.ts';
import { useRobots, type Robot } from '../../../hooks/useRobots.ts';
import { useRobotScans, type ProcessingState, type Scan } from '../../../hooks/useScans.ts';

import { SurfaceShell, BackLink } from '../../../components/SurfaceShell.tsx';
import { Panel, Section, Eyebrow } from '../../../components/Panel.tsx';
import {
  ReadPanel,
  SkeletonPlot,
  SkeletonRail,
  SkeletonTable,
  StoppedContext,
  useRead,
  type SkeletonColumn,
  type SkeletonLane,
} from '../../../components/states/index.ts';
import { Button, IconButton } from '../../../components/Button.tsx';
import { Input } from '../../../components/Field.tsx';
import { DataTable, type Column } from '../../../components/DataTable.tsx';
import { SegmentedControl } from '../../../components/Toolbar.tsx';
import { Ribbon, TimeAxis, type RibbonLane, type RibbonSegment } from '../../../components/Ribbon.tsx';
import { Plot } from '../../../components/Plot.tsx';
import { StatusBadge, StateLabel } from '../../../components/StatusBadge.tsx';
import { LivenessBadge } from '../../../components/LivenessBadge.tsx';
import { StatusDot } from '../../../components/StatusDot.tsx';
import { LiveTag } from '../../../components/Freshness.tsx';
import { Empty } from '../../../components/Empty.tsx';
import { Chip } from '../../../components/Chip.tsx';
import { Timestamp } from '../../../components/Timestamp.tsx';
import { InlineNotice } from '../../../components/Toast.tsx';
import { useNow } from '../../../components/useNow.ts';
import { level, levelTone, STATUS_STATE, type Tone } from '../../../components/state.ts';
import type { RobotStatus } from '../../../hooks/useRobots.ts';
import { bytes, duration, latlon, unitIsTight, utc, NO_VALUE } from '../../../components/format.ts';
import { Unit } from '../../../components/Unit.tsx';
import { scanState } from '../../../components/scanState.ts';
import { axisClock, frac } from '../../../components/timeScale.ts';
import { Icon, type IconName } from '../../../shell/Icon.tsx';

import { mediaConfig, type MediaConfig } from '../../../media/config.ts';
import { useVideoSession, type LiveVideo } from '../../../media/useVideoSession.ts';

import styles from './Teleop.module.css';

/* ==========================================================================
   TELEOPERATION — the marquee surface, and the one where an amateur console
   gets somebody hurt.

   PRD-0002 defines the machine precisely: H.264 video DOWN over a LiveKit
   WebRTC session, continuous operator control UP on a LOSSY data channel on
   the SAME session, exclusive control via a backend-arbitrated lease
   (request → grant → hold → release/handoff), and a safety model of deadman +
   latency watchdog + connection-loss safe-stop in which the ROBOT is
   authoritative and this console only assists.

   None of that media plane is built yet. So the single hardest requirement on
   this file is the one THE BAR §11.10 states as an absolute:

       "The most common way a teleop console lies is by looking alive when it
        is not… an invented latency figure is the single worst defect
        available to this product: it is the one number an operator trusts to
        decide whether to move a machine, and a fabricated one will be
        believed."

   So: no fake feed, no seeded latency, no placeholder gauge readings. Every
   teleop-plane instrument on this screen renders its honest empty state — a
   --void stage naming the robot, the endpoint and the state; gauge tracks with
   no arc and an em dash; a link drill-down whose twelve fields are all em
   dashes with the reason printed at the top; a lease control that is DISABLED
   and says why.

   And everything the platform DOES have is real and live:
     · battery / cpu / disk / temperature / speed / heading / position, and the
       track behind the robot, from the telemetry stream;
     · liveness, cert, morphology, revision, board, sim-vs-real, site, from the
       registry;
     · the command queue and its per-command audit trail — including a
       genuinely MEASURED dispatch round-trip distribution (issue → first
       transition), which is the closest thing this platform has to a control
       latency and is exactly the number that argues for building teleop at
       all: it is measured in MINUTES.
   ========================================================================== */

/* --------------------------------------------------------------------------
   The media plane, read the way auth/config.ts and realtime/config.ts read
   theirs: entirely from the environment, and absent means ABSENT. Naming the
   variable on screen is rerun-mcap-lidar-dual-3d-timeline.png's discipline —
   "Waiting for data on rerun+http://127.0.0.1:9876/proxy…" — the endpoint and
   the state, in the chrome, in mono.
   -------------------------------------------------------------------------- */

/* The media plane moved out of this file: PRD-0010 replaced PRD-0002 D1/D2's
   LiveKit with IVS Real-Time over WHIP, and the browser is now handed a
   subscribe-only participant token rather than a URL to connect to. Config,
   session lifecycle and the WebRTC subscription live in src/media/. */

/* The shape of the rectangle the picture will land in.

   THE BAR §11.2: "the video pane's aspect follows the stream's." Nothing has
   advertised a track, so there is no stream to follow, and the fallback is
   the one the platform has actually committed to — PRD-0002 D11 pins 16:9
   H.264 for v1. That is a declared intent rather than a measurement, so the
   frame carries a note saying so; the moment a track advertises its own
   resolution this is replaced by it and the note goes away. */
/** `/depth_cam/rgb/image_raw` -> `rgb`. The full path is in the menu and the
    tooltip; the control itself is ~100px and a topic path clipped mid-word says
    less than nothing. */
/* Rail collapse (#299), remembered per viewer.

   Someone who mostly watches should not re-collapse this every session, and the
   preference is a local convenience rather than fleet state -- so localStorage,
   and a read that cannot throw. Private windows and blocked site-data both
   return nothing here, which is the expanded default and correct. */
const RAIL_KEY = 'f3.teleop.railCollapsed';

function readRailCollapsed(): boolean {
  try {
    return window.localStorage.getItem(RAIL_KEY) === '1';
  } catch {
    return false;
  }
}

function writeRailCollapsed(v: boolean): void {
  try {
    window.localStorage.setItem(RAIL_KEY, v ? '1' : '0');
  } catch {
    /* Storage unavailable: the toggle still works for this session. */
  }
}

function shortCamera(topic: string | null): string {
  if (!topic) return 'auto';
  const parts = topic.split('/').filter(Boolean);
  const i = parts.indexOf('image_raw');
  return (i > 0 ? parts[i - 1] : parts[parts.length - 1]) ?? topic;
}

/** One camera the robot has said it will serve (C5/D7, #245). */
interface ServableCamera {
  topic: string;
  /** The robot's own default — RGB. At most one is true. */
  isDefault: boolean;
}

const STREAM_AR = {
  css: '16 / 9',
  label: '16:9',
  why:
    'No track is advertised, so the frame is drawn at the aspect PRD-0002 D11 pins for v1 '
    + '(16:9 H.264). It is redrawn at the track\u2019s own resolution as soon as one is subscribed.',
} as const;

/* --------------------------------------------------------------------------
   The control lease — PRD-0002 §2/D4, THE BAR §11.6.

   Seven states, and a critic is entitled to ask to see each. The ladder is
   rendered in full, always, with the live one marked: an operator must be
   able to see not just where the lease IS but what the machine can do, and a
   state machine that only ever shows its current node is a state machine you
   have to have read the PRD to trust.
   -------------------------------------------------------------------------- */

type LeaseState =
  | 'no-session'
  | 'observing'
  | 'requesting'
  | 'held-other'
  | 'held'
  | 'degraded'
  | 'lost';

interface LeaseStep {
  id: LeaseState;
  label: string;
  /** What has to be true to be in this state. */
  condition: string;
}

const LEASE_LADDER: readonly LeaseStep[] = [
  { id: 'no-session', label: 'NO SESSION', condition: 'no media plane' },
  { id: 'observing', label: 'OBSERVING', condition: 'stream up · controls inert' },
  { id: 'requesting', label: 'REQUESTING', condition: 'grant in flight' },
  { id: 'held-other', label: 'HELD BY OTHER', condition: 'another operator drives' },
  { id: 'held', label: 'HELD BY YOU', condition: 'deadman required' },
  { id: 'degraded', label: 'HELD · DEGRADED', condition: 'watchdog · rate gated' },
  { id: 'lost', label: 'REVOKED / LOST', condition: 'robot safe-stops itself' },
];

interface Lease {
  state: LeaseState;
  /** Why the operator cannot take control. Printed on the disabled switch. */
  reason: string;
  /** Present only once a lease genuinely exists. */
  holder: string | null;
  since: string | null;
}

/**
 * Resolve the lease from what is actually knowable. With no media plane there
 * is exactly one possible answer, and the reason is the useful half — THE BAR
 * §11.10: "a disabled control with no reason is worse than an enabled one
 * that fails."
 */
function resolveLease(robot: Robot, media: MediaConfig | null): Lease {
  if (media === null) {
    return {
      state: 'no-session',
      reason: 'media plane not configured',
      holder: null,
      since: null,
    };
  }
  if (robot.liveness === 'offline' || robot.liveness === 'provisioning') {
    return { state: 'no-session', reason: `robot ${robot.liveness}`, holder: null, since: null };
  }
  /* A session could exist here; nothing in this build can open one, so the
     honest terminal answer is still "no session" and the reason names the
     missing half rather than pretending at a lease. */
  return { state: 'no-session', reason: 'no session on this robot', holder: null, since: null };
}

/* --------------------------------------------------------------------------
   THE BAR §13 tell 27 — one global "connected" light standing in for video,
   control, telemetry and dispatch is a defect: the operator has to see WHICH
   one degraded. This surface answers it per stream, but NOT in a rail list.
   Round 4's `Channels` block was a fifth copy of four answers that each
   already exist on the panel that owns them —

     video   → the /VIDEO_TRACK dot on the vitals strip, and the stage line
     control → the /CTRL_CHAN dot beside it
     telemetry → the HUD chip and age in the stage header
     dispatch → the Push/Polling tag in the command queue's own header

   — and two of its four rows said "no session" for the third and fourth time
   on one screen. Attaching each answer to the surface it describes is what
   tell 27 actually asks for; aggregating them into a distant list is not.
   -------------------------------------------------------------------------- */

/* --------------------------------------------------------------------------
   Dispatch round trip, MEASURED off the command audit trail.

   PRD-0002's whole argument is that the discrete command queue is right for
   dispatch and wrong for driving. This is that argument as a number: the time
   from `issued` to the command's first transition, over however many commands
   the audit holds. It is not a teleop latency and is never labelled as one.
   -------------------------------------------------------------------------- */

interface AuditEvent {
  id: number;
  command_id: number;
  robot_id: string;
  ts: string;
  event: string;
  operator: string;
  status: string;
  result: Record<string, unknown> | null;
  fields: Record<string, unknown>;
}

interface Rtt {
  n: number;
  medianMs: number | null;
  minMs: number | null;
  maxMs: number | null;
  lastAt: string | null;
}

function dispatchRtt(events: readonly AuditEvent[] | undefined): Rtt {
  if (!events || events.length === 0) return { n: 0, medianMs: null, minMs: null, maxMs: null, lastAt: null };
  const issued = new Map<number, number>();
  const first = new Map<number, { t: number; iso: string }>();
  for (const e of events) {
    const t = Date.parse(e.ts);
    if (!Number.isFinite(t)) continue;
    if (e.event === 'issued') {
      const prev = issued.get(e.command_id);
      if (prev === undefined || t < prev) issued.set(e.command_id, t);
    } else {
      const prev = first.get(e.command_id);
      if (prev === undefined || t < prev.t) first.set(e.command_id, { t, iso: e.ts });
    }
  }
  const samples: number[] = [];
  let lastAt: string | null = null;
  let lastT = -Infinity;
  for (const [cid, t0] of issued) {
    const hit = first.get(cid);
    if (hit === undefined || hit.t < t0) continue;
    samples.push(hit.t - t0);
    if (hit.t > lastT) {
      lastT = hit.t;
      lastAt = hit.iso;
    }
  }
  if (samples.length === 0) return { n: 0, medianMs: null, minMs: null, maxMs: null, lastAt: null };
  samples.sort((a, b) => a - b);
  const mid = Math.floor(samples.length / 2);
  const median =
    samples.length % 2 === 1
      ? (samples[mid] as number)
      : ((samples[mid - 1] as number) + (samples[mid] as number)) / 2;
  return {
    n: samples.length,
    medianMs: median,
    minMs: samples[0] as number,
    maxMs: samples[samples.length - 1] as number,
    lastAt,
  };
}

function useCommandAudit(robotId: string | undefined) {
  return useQuery({
    queryKey: ['audit', 'commands', robotId] as const,
    queryFn: async (): Promise<AuditEvent[]> => {
      const r = await apiFetch(
        `/api/audit/commands?robot_id=${encodeURIComponent(robotId as string)}&limit=60`,
      );
      if (!r.ok) throw new Error(`status ${r.status}`);
      const body = (await r.json()) as { events?: AuditEvent[] };
      return body.events ?? [];
    },
    enabled: !!robotId,
    refetchInterval: 20_000,
    refetchIntervalInBackground: true,
  });
}

/* --------------------------------------------------------------------------
   Small helpers over the telemetry stream.
   -------------------------------------------------------------------------- */

/**
 * ONE staleness threshold, derived ONCE, for every instrument on this screen
 * that is downstream of the telemetry stream.
 *
 * THE BAR §11.10: "the most common way a teleop console lies is by looking
 * alive when it is not." The failure this constant exists to prevent is
 * precise and was the worst defect in the previous round of this file: an
 * offline robot whose newest reading is eleven hours old rendered a vitals
 * strip that was byte-for-byte identical to a live robot's, and a map pose in
 * the live accent hue. Both were confident claims about *now* made out of
 * data from last night.
 *
 * So `sampleOf()` is the single source, it is computed in the root component,
 * and it is threaded into the HUD tapes, the vitals rings AND the map marker.
 * Nothing on this screen may decide for itself what "fresh" means.
 *
 * 180s is three minutes — an order of magnitude past the slowest cadence the
 * fleet reports at (2m 24s on the fixtures), so a robot that is merely slow is
 * not libelled, and one that has actually stopped talking is caught inside one
 * missed report.
 */
const STALE_AFTER_S = 180;

interface SampleAge {
  /** Seconds since the newest reading. null = this robot has never reported. */
  ageS: number | null;
  /** The reading exists but is old enough that nothing derived from it is a
   *  live claim. Distinct from "no reading at all", which is `ageS === null`. */
  stale: boolean;
  /** `10h58m` / `4m12s` / `61s` — the age with the space closed up, so it can
   *  ride INSIDE a value cell without doubling the cell's width. */
  age: string;
}

function isStale(ageS: number | null): boolean {
  return ageS !== null && ageS > STALE_AFTER_S;
}

function sampleOf(ageS: number | null): SampleAge {
  return {
    ageS,
    stale: isStale(ageS),
    age: ageS === null ? NO_VALUE : duration(ageS * 1000).replace(/\s+/gu, ''),
  };
}

/* --------------------------------------------------------------------------
   THE FRAME — the one instant every instrument on this screen is reading.

   Round 4 had four independent live readouts: the HUD tapes read the
   registry, the vitals rings read the newest reading, the map read the
   registry pose and the timeline read whatever the pointer happened to be
   over. Four answers to "when". This type collapses them to one.

   `frameAt()` is called ONCE in the root, with `now` while live and with the
   playhead while the operator is scrubbing, and the object it returns is
   threaded into the stage, the vitals strip, the drive readout, the map and
   the ledger. Nothing downstream is allowed to ask what time it is.
   -------------------------------------------------------------------------- */

interface Frame {
  /** The instant being shown: `now` while live, the playhead while replaying. */
  t: number;
  /** The operator has dragged the playhead off live. */
  replay: boolean;
  /** Newest reading at or before `t`. */
  reading: TelemetryReading | undefined;
  /** Age of `reading` MEASURED AGAINST `t` — the same fact live or replaying. */
  sample: SampleAge;
  /**
   * A reading exists close enough to `t` to speak for it. False means the
   * cursor is inside a coverage hole, and in replay that is the answer the
   * bound instruments must give — holding up the last value you happen to
   * have is how a console lies about an instant it has no data for.
   */
  covered: boolean;
}

function frameAt(
  readings: readonly TelemetryReading[],
  t: number,
  replay: boolean,
  cadenceMs: number | null,
): Frame {
  const cad = cadenceMs !== null && cadenceMs > 0 ? cadenceMs : 60_000;
  let hit: TelemetryReading | undefined;
  /* `readings` is newest-first, so the first one at or before the cursor is
     the one that speaks for it. */
  for (const r of readings) {
    const rt = Date.parse(r.ts);
    if (Number.isFinite(rt) && rt <= t) {
      hit = r;
      break;
    }
  }
  const ageS = hit === undefined ? null : Math.max(0, (t - Date.parse(hit.ts)) / 1000);
  return {
    t,
    replay,
    reading: hit,
    sample: sampleOf(ageS),
    covered: ageS !== null && ageS * 1000 <= cad * 2.5,
  };
}

/**
 * The reading a bound instrument may render as a VALUE at this frame.
 *
 * Live: always the newest reading, however old — an offline robot's last
 * readings are still the truth about it, and the staleness treatment (dimmed
 * arc, `--unknown`, the age printed in the cell) is what stops them reading
 * as a claim about now.
 *
 * Replay: only a reading that actually covers the cursor. Scrubbing into a
 * coverage hole must produce em dashes, not the last value before the hole.
 */
function shownReading(frame: Frame): TelemetryReading | undefined {
  return frame.replay && !frame.covered ? undefined : frame.reading;
}

function payloadNum(t: TelemetryReading | undefined, key: string): number | null {
  if (t === undefined) return null;
  const v = t.payload[key];
  return typeof v === 'number' ? v : null;
}

function metaStr(robot: Robot, key: string): string | null {
  const v = robot.meta[key];
  return typeof v === 'string' ? v : null;
}

interface ProfileCommand {
  command: string;
  hazard: string;
}

/** The morphology's declared verbs, with their hazard class (PRD-0001 §5). */
function readProfile(robot: Robot): ProfileCommand[] {
  const raw = robot.command_profile?.['commands'];
  if (raw === null || raw === undefined || typeof raw !== 'object') return [];
  return Object.entries(raw as Record<string, unknown>)
    .map(([command, spec]) => ({
      command,
      hazard:
        typeof (spec as { hazard?: unknown } | null)?.hazard === 'string'
          ? ((spec as { hazard: string }).hazard)
          : 'benign',
    }))
    .sort((a, b) => a.command.localeCompare(b.command));
}

/* ==========================================================================
   INSTRUMENTS
   ========================================================================== */

/**
 * One vitals ring — 28px OD on a 3px stroke, per THE BAR §11.12.
 *
 * The track is a fixed neutral and NEVER carries state (measured 4/4 around
 * every ring in formant-teleop-ground-station.png). The arc is one hue for
 * every reading inside its limits and changes ONLY on the gauge that has
 * breached: tell 28 is "with every metric inside its threshold, how many hues
 * are in the vitals strip? more than one is a defect."
 *
 * That one hue is `--nominal` steel rather than THE BAR's suggested `--ok`
 * blue, because this console already spends colour that way everywhere else
 * (primitives rule 7, `boundedTone`) and a teleop strip in a different palette
 * from the battery column two clicks away is a worse lie than a shade.
 *
 * The published limit is drawn as a 1px tick ON the track, so the threshold
 * is on the screen rather than in the operator's head (tell 15).
 *
 * STALE is the third rendering, and it is not cosmetic. A reading older than
 * STALE_AFTER_S is not a measurement of now, so the ring stops asserting one:
 * the arc drops out of the semantic palette to `--unknown`, arc and value dim
 * to 40%, and the threshold verdict is WITHDRAWN — a 10-hour-old 8% battery
 * must not paint the same red as a live one, because red here means "go and
 * do something about it" and there is nothing to do about a number from last
 * night except go and find out why the robot stopped talking.
 */
function VitalRing({
  label,
  value,
  unit,
  digits = 0,
  min = 0,
  max = 100,
  warn,
  bad,
  invert = false,
  stale = false,
  age,
  note,
  onClick,
  open,
  shortcut,
}: {
  label: string;
  value: number | null;
  unit: string;
  digits?: number | undefined;
  min?: number | undefined;
  max?: number | undefined;
  warn: number;
  bad: number;
  invert?: boolean | undefined;
  /** The sample this reading came from is too old to be a claim about now. */
  stale?: boolean | undefined;
  /** `10h58m` — printed INSIDE the cell whenever `stale`, so the number is
   *  never readable without its age. */
  age?: string | undefined;
  /** Replaces the value when there is nothing to read — `no session`. */
  note?: string | undefined;
  onClick?: (() => void) | undefined;
  /** For an actionable ring: whether its drill-down is currently open. */
  open?: boolean | undefined;
  shortcut?: string | undefined;
}) {
  const known = value != null && Number.isFinite(value);
  const lv = level(known && !stale ? value : null, warn, bad, invert);
  const tone = lv === 'warn' || lv === 'bad' ? levelTone(lv) : 'nominal';
  const R = 12.5;
  const C = 2 * Math.PI * R;
  const frac = known ? Math.max(0, Math.min(1, (value - min) / (max - min))) : 0;
  const limitFrac = Math.max(0, Math.min(1, (bad - min) / (max - min)));
  const limitAngle = limitFrac * 360 - 90;
  const tick = {
    x1: 14 + (R - 2.4) * Math.cos((limitAngle * Math.PI) / 180),
    y1: 14 + (R - 2.4) * Math.sin((limitAngle * Math.PI) / 180),
    x2: 14 + (R + 2.4) * Math.cos((limitAngle * Math.PI) / 180),
    y2: 14 + (R + 2.4) * Math.sin((limitAngle * Math.PI) / 180),
  };

  const body = (
    <>
      <svg className={styles.ring} width={28} height={28} viewBox="0 0 28 28" aria-hidden="true">
        <circle className={styles.ringTrack} cx="14" cy="14" r={R} strokeWidth={3} />
        {known && frac > 0 && (
          <circle
            className={styles.ringArc}
            cx="14"
            cy="14"
            r={R}
            strokeWidth={3}
            strokeDasharray={`${(frac * C).toFixed(2)} ${((1 - frac) * C).toFixed(2)}`}
            transform="rotate(-90 14 14)"
          />
        )}
        <line className={styles.ringLimit} {...tick} />
      </svg>
      <span className={styles.ringLabel}>{label}</span>
      <span className={styles.ringValue} data-toned={lv === 'warn' || lv === 'bad'}>
        {known ? value.toFixed(digits) : NO_VALUE}
        {known && <Unit unit={unit} className={styles.ringUnit} />}
        {/* The age rides in the cell, not beside the strip: BAT 46% and
            BAT 46% · 10h58m are two different claims and only the second is
            true of a robot that stopped talking last night. */}
        {known && stale && age !== undefined && <span className={styles.ringAge}>· {age}</span>}
      </span>
    </>
  );

  const title = known
    ? `${label} ${value.toFixed(digits)}${unit} — limit ${bad}${unit}${
        stale ? ' — STALE: this is the last reading, not a current one' : ''
      }`
    : `${label} — ${note ?? 'no reading'}`;

  if (onClick === undefined) {
    return (
      <div
        className={styles.vital}
        data-tone={tone}
        data-unknown={!known}
        data-stale={stale}
        title={title}
      >
        {body}
      </div>
    );
  }
  return (
    <button
      type="button"
      className={styles.vital}
      data-tone={tone}
      data-unknown={!known}
      data-stale={stale}
      data-actionable="true"
      data-open={open === true}
      aria-expanded={open === true}
      title={`${title}${shortcut === undefined ? '' : ` (${shortcut})`}`}
      onClick={onClick}
    >
      {body}
      <Icon name={open === true ? 'chevron-down' : 'chevron-up'} size={12} />
    </button>
  );
}

/**
 * Named boolean topics as status dots — THE BAR §11.5, the most transferable
 * idea in formant-teleop-ground-station.png and one no other reference has.
 *
 * Three renderings, three meanings, and the third is the whole point here: a
 * topic that has NEVER published must not look like a topic publishing false.
 * `/VIDEO_TRACK` and `/CONTROL_CHANNEL` are hollow at 40% because this
 * platform has never carried either, and that is the truest thing this strip
 * can say.
 *
 * The label is the topic path, not a human paraphrase: greppable, and it maps
 * to something the operator can subscribe to in a debug tool.
 */
function TopicDot({
  topic,
  value,
  title,
}: {
  topic: string;
  /** null = never published. */
  value: boolean | null;
  title: string;
}) {
  const state = value === null ? 'absent' : value ? 'true' : 'false';
  return (
    <span className={styles.topic} data-state={state} title={title}>
      <StatusDot tone={value === true ? 'ok' : 'neutral'} hollow={value === null} />
      <span className={styles.topicName}>{topic}</span>
      <span className={styles.topicValue}>
        {value === null ? 'never' : value ? 'true' : 'false'}
      </span>
    </span>
  );
}

/* ==========================================================================
   THE VIDEO STAGE
   ========================================================================== */

interface StageProps {
  robot: Robot;
  media: MediaConfig | null;
  /** The live subscription: state, reason, track and MEASURED stats (#244). */
  video: LiveVideo;
  /** Camera tracks the agent has advertised on this session. Empty is the
   *  truth today, and it is what collapses the stage to its void strip. */
  cameras: readonly ServableCamera[];
  /** The topic the session is actually on, or null while it is the robot's own. */
  track: string | null;
  /** Where to pin the menu: the selector's own rect, or null when closed. */
  camAnchor: { x: number; y: number } | null;
  onToggleCameras: (rect: { x: number; y: number }) => void;
  onPickCamera: (topic: string) => void;
  /** D11: whether the NEXT session opens with recording on. Settable only
   *  while video is off — recording is a property of a session, and a session
   *  already running belongs to everyone watching it. */
  record: boolean;
  onToggleRecord: () => void;
  /** The one instant this whole screen is showing — see frameAt(). */
  frame: Frame;
  /** Stage-over-the-page. Lifted OUT of the stage because the layer has to be
   *  positioned against the DECK — the page — and the stage's own column is a
   *  positioned, z-indexed grid item that would otherwise be the containing
   *  block. The deck reads this to release that column while the layer is up. */
  expanded: boolean;
  onExpandedChange: (v: boolean) => void;
  linkOpen: boolean;
  onOpenLink: () => void;
  onAction: (id: MenuAction) => void;
}

/**
 * Why there is no picture, as facts rather than as an apology.
 *
 * Every row is either a live reading or a named PRD-0002 gate, so the operator
 * gets an answer to their actual question — "what would have to be true for me
 * to see this robot?" — instead of a spinner over a capability that is not
 * built.
 *
 * It lives in the RAIL, directly under the lease control whose disabled reason
 * it expands, and NOT on the stage: THE BAR §11.10 gives the no-session stage
 * exactly one mono line and nothing else, and a five-row card centred in the
 * void is the centred empty state that rule exists to forbid (tell 3).
 */
function Preconditions({
  robot,
  media,
  operator,
  authed,
  telemetryAgeS,
}: {
  robot: Robot;
  media: MediaConfig | null;
  operator: string;
  authed: boolean;
  telemetryAgeS: number | null;
}) {
  const linkOk = robot.liveness === 'live' || robot.liveness === 'backfilling';
  /* Three gates, not five: `camera track` is the /VIDEO_TRACK dot in the
     stage's topic strip and `control lease` is the ladder immediately below
     this list, and a rail that says the same thing three times is a rail that
     has run out of room for the thing it has not said yet. */
  const rows: { label: string; ok: boolean; value: string }[] = [
    { label: 'operator', ok: authed, value: operator },
    {
      label: 'robot link',
      ok: linkOk,
      value: `${robot.liveness}${telemetryAgeS === null ? '' : ` · ${duration(telemetryAgeS * 1000)}`}`,
    },
    {
      label: 'media endpoint',
      ok: media !== null,
      value: media === null ? 'VITE_VIDEO_ENABLED unset' : media.label,
    },
  ];
  return (
    <ul className={styles.precon} aria-label="Session preflight">
      {rows.map((r) => (
        <li key={r.label} className={styles.preconRow} data-ok={r.ok}>
          <Icon name={r.ok ? 'check' : 'close'} size={12} />
          <span className={styles.preconLabel}>{r.label}</span>
          <span className={styles.preconValue}>{r.value}</span>
        </li>
      ))}
    </ul>
  );
}

type MenuAction = 'detail' | 'copy-id' | 'copy-fix' | 'safe-stop' | 'request'
  | 'start-video' | 'stop-video';

const STAGE_MENU: readonly { id: MenuAction; label: string; kbd: string; icon: IconName }[] = [
  { id: 'detail', label: 'Open robot detail', kbd: '⇧D', icon: 'external-link' },
  { id: 'copy-id', label: 'Copy robot id', kbd: '⇧C', icon: 'copy' },
  { id: 'copy-fix', label: 'Copy WGS84 fix', kbd: '⇧F', icon: 'crosshair' },
  { id: 'start-video', label: 'Start video', kbd: '⇧V', icon: 'video' },
  { id: 'stop-video', label: 'Stop video', kbd: '⇧B', icon: 'video-off' },
  { id: 'request', label: 'Request control', kbd: '⇧U', icon: 'unlock' },
  { id: 'safe-stop', label: 'Safe-stop robot', kbd: '⇧X', icon: 'stop' },
];

/**
 * THE STAGE — ONE geometry, in every state.
 *
 * THE BAR §11.10 is a pass/fail and it does not have a no-stream exemption:
 * `video = --void + one mono line naming robot, endpoint, state`. So the
 * stage is always the rectangle the H.264 track will land in, drawn at the
 * stream's own aspect (`STREAM_AR` — PRD-0002 D11 pins 16:9 H.264 for v1,
 * and nothing has advertised a track to override it), `contain`-fitted into
 * the pane in pure CSS so letterbox waste stays under §11.2's 15% at every
 * width this console is used at.
 *
 * Round 5 collapsed it to a 132px strip on the argument that a stage with no
 * stream is 100% letterbox. That reasoning is wrong twice: the rectangle is
 * not letterbox, it is the reserved frame — the operator has to be able to
 * see how much of the pane the picture will claim and that nothing will move
 * when it arrives (§7) — and a teleoperation console with zero picture pixels
 * is not a teleoperation console. The honest empty state is a confident empty
 * frame, not a missing one.
 *
 * On the frame, and only these: the heading tape across the top and the speed
 * tape up the leading edge, both stroke-only with one small opaque readout
 * box each that prints its own reference (`N`, `M/S GND`). Everything that is
 * not a tape — authority, lease, codec, frame size, frame rate, REC — is on
 * the 24px bar beneath it, never on the image (§11.2).
 */
function VideoStage({
  robot,
  media,
  video,
  cameras,
  track,
  camAnchor,
  onToggleCameras,
  onPickCamera,
  record,
  onToggleRecord,
  frame,
  expanded,
  onExpandedChange,
  linkOpen,
  onOpenLink,
  onAction,
}: StageProps) {
  const [menu, setMenu] = useState<{ x: number; y: number } | null>(null);
  const stageRef = useRef<HTMLDivElement | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);

  // srcObject is not an attribute, so it is attached imperatively. Cleared on
  // teardown so a dead track cannot leave its last frame on screen looking live
  // — a frozen picture that reads as a feed is the defect this surface exists
  // to avoid.
  /* Geometry comes from the ELEMENT, not the track.
     `RemoteStageStream.mediaStreamTrack.getSettings()` is what the hook reads,
     and on a REMOTE track Chrome returns `{deviceId, resizeMode}` and nothing
     else — measured on a live 640x360 stream, no width, no height, ever. So
     `video.stats.width` is permanently null for a subscriber, which is why the
     stage always drew itself at the DECLARED 16:9 and never at the track's own
     shape, and why the frame-size fact could never fill in.

     The decoder knows. `videoWidth`/`videoHeight` are the decoded frame, and
     `resize` fires whenever the publisher changes it mid-session — which is
     exactly what an adaptive profile (#246) will do. Still measured, still
     never declared: null until a frame has actually been decoded. */
  const [frameSize, setFrameSize] = useState<{ w: number; h: number } | null>(null);

  /* EXPANDED: the stage over the whole page, at full width less a margin.
     No portal and no second <video>. The <video> owns `srcObject`, attached
     imperatively — re-parenting it (a portal, or rendering a second element)
     either tears the stream down through the effect's cleanup or decodes the
     same track twice. So nothing moves: the SAME element is promoted with
     `position: fixed`, which is viewport-relative here because no ancestor of
     the stage carries a transform, filter or containment (checked). */
  const exitRef = useRef<HTMLButtonElement | null>(null);
  const returnFocus = useRef<HTMLElement | null>(null);
  const setExpanded = onExpandedChange;

  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;
    el.srcObject = video.stream;
    if (video.stream) void el.play().catch(() => undefined);

    const read = () => {
      // 0x0 is "not decoded yet", not a reading. Never publish it as one.
      if (el.videoWidth > 0 && el.videoHeight > 0) {
        setFrameSize({ w: el.videoWidth, h: el.videoHeight });
      }
    };
    read();
    el.addEventListener('loadedmetadata', read);
    el.addEventListener('resize', read);

    return () => {
      el.removeEventListener('loadedmetadata', read);
      el.removeEventListener('resize', read);
      el.srcObject = null;
      // A stale size outliving its stream would let the next session open
      // claiming the last one's shape.
      setFrameSize(null);
    };
  }, [video.stream]);

  const live = video.state === 'live' || video.state === 'degraded';

  /* A modal that outlives its reason is a trap: if the session drops there is
     no picture to be full-page about, and the operator is left holding a black
     rectangle over their console. Collapsing is the same judgement the rest of
     this file makes about stale pictures. */
  useEffect(() => {
    if (expanded && !live) setExpanded(false);
  }, [expanded, live, setExpanded]);

  useEffect(() => {
    if (!expanded) return;
    // Escape is the expected way out of a modal layer, and it is the ONLY way
    // out for someone who reached the expand control from the keyboard.
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        setExpanded(false);
      }
    };
    window.addEventListener('keydown', onKey, true);
    // Focus goes to the way out, not into the layer at large — otherwise a
    // keyboard user is inside a dialog with their focus still behind it.
    exitRef.current?.focus();
    return () => {
      window.removeEventListener('keydown', onKey, true);
      returnFocus.current?.focus();
    };
  }, [expanded, setExpanded]);
  // The frame's shape is a claim. Once a track advertises its own geometry the
  // rectangle is drawn at THAT, and the "declared, not measured" note goes.
  const measuredAr = frameSize ? `${frameSize.w} / ${frameSize.h}` : null;

  useEffect(() => {
    if (menu === null) return;
    const close = () => setMenu(null);
    window.addEventListener('pointerdown', close);
    window.addEventListener('keydown', close);
    return () => {
      window.removeEventListener('pointerdown', close);
      window.removeEventListener('keydown', close);
    };
  }, [menu]);

  /* The subscribed track, or null. It names the frame and fills the picker;
     it no longer decides the stage's geometry, because §11.10 gives the
     no-session stage the same rectangle as the live one. */
  /* `track` arrives as a prop — it is the topic the SESSION is on, which the
     stage cannot derive. It is deliberately not `cameras[0]`: the list is
     sorted, so on this robot that is the DEPTH camera while the picture is RGB,
     and a control that names the wrong camera is the same defect as an invented
     latency figure. */
  const sample = frame.sample;
  const stale = sample.stale;
  /* One reading, resolved once, for every instrument on this strip. In replay
     a cursor inside a coverage hole yields `undefined` and every value below
     becomes an em dash — see shownReading(). */
  const r = shownReading(frame);
  const cpu = r?.cpu_pct ?? null;
  const disk = r?.disk_used_pct ?? null;
  const temp = r?.temp_c ?? null;
  const bat = r?.battery_pct ?? null;
  const sats = payloadNum(r, 'gnss_sats');
  const motionOk = robot.status !== 'error' && robot.status !== 'maintenance';
  const sim = metaStr(robot, 'kind') === 'sim';
  const openMenu = (e: { clientX: number; clientY: number; preventDefault: () => void }) => {
    e.preventDefault();
    const box = stageRef.current?.getBoundingClientRect();
    setMenu({ x: e.clientX - (box?.left ?? 0), y: e.clientY - (box?.top ?? 0) });
  };

  const honest = (
    <span className={styles.noSignalState}>
      <StatusDot tone="warn" hollow />
      {/* One text node, so a narrow frame ellipses the line instead of
          clipping it mid-glyph — `text-overflow` does not apply to a flex
          container, only to the block inside it. */}
      <span className={styles.noSignalText}>
        {robot.robot_id} <em>·</em>{' '}
        {media === null ? 'VITE_VIDEO_ENABLED unset' : media.label}{' '}
        <em>·</em> awaiting stream
      </span>
    </span>
  );

  /* The stream facts — the codec PRD-0002 D11 pins, and three em dashes that
     will be a frame size, a frame rate and a recording state. They ride the
     lower letterbox in `full` and the single band row in `band`; either way
     they are on the stage's own chrome, never in a menu. */
  /* The video control, VISIBLE.
     It was previously reachable only by right-clicking the stage, which is not
     an affordance — it is a secret. An operator cannot discover a context menu
     on a black rectangle, and the first person to try this could not start a
     session because there was nothing on screen to press. The context-menu
     entries stay as an accelerator; this is the control. */
  const videoBusy = video.state === 'starting' || video.state === 'connecting'
    || video.state === 'reconnecting';
  const videoOn = videoBusy || video.state === 'live' || video.state === 'degraded';
  const streamFacts = (
    <>
      <button
        type="button"
        className={styles.videoBtn}
        data-on={videoOn}
        onClick={() => (videoOn ? video.stop() : video.start())}
        title={videoOn
          ? 'Stop watching. The stream ends when the last watcher leaves.'
          : 'Ask this robot to publish its camera. Video is on demand — an unwatched robot spends no CPU and no bytes on it.'}
      >
        <Icon name={videoOn ? 'video-off' : 'video'} size={12} />
        {videoOn ? 'Stop video' : 'Start video'}
      </button>
      {/* D11: RECORD, and the distinction it has to carry.
          Off by default and billed per hour, so the control is deliberately
          quiet rather than inviting. Disabled while a session runs: recording
          is fixed when the session opens, and a joiner flipping it would be
          reconfiguring a stream their colleagues are already watching — the
          API refuses that for the same reason.

          The label says "Record clip", never "Record": what this captures is
          the cloud-side copy, and the robot's own capture is the record of
          these minutes.

          A SWITCH rather than a button (THE BAR 11.6), for the reason the
          control lease is one: a switch shows its state when nobody is looking
          at it. As a button it had to spell the state into its own label —
          "Record clip: on", "Not recording" — so the control changed width
          every time it changed state, in the one strip on this surface that
          cannot spare the pixels. The label is fixed now and the knob carries
          the state. */}
      <button
        type="button"
        role="switch"
        aria-checked={videoOn ? video.session?.recording === true : record}
        className={styles.recSwitch}
        data-on={videoOn ? video.session?.recording === true : record}
        disabled={videoOn}
        onClick={onToggleRecord}
        title={videoOn
          ? (video.session?.recording === true
            ? 'This session is being recorded to the cloud. It ends with the session. NOT the field record — the robot\u2019s own capture is.'
            : 'This session is not being recorded. Recording is fixed when a session opens; stop and restart to change it.')
          : (record
            ? 'The next session will be recorded and kept for 30 days. This is the CLOUD-SIDE copy — it loses frames exactly when the link does. The robot\u2019s own capture stays the record.'
            : 'Record the next session to the cloud, kept 30 days. Off by default: it is billed by the hour.')}
      >
        <span className={styles.recTrack} aria-hidden="true">
          <span className={styles.recKnob} />
        </span>
        Record clip
      </button>
      {/* THE ONLY GAP IN THE BAR, and it sits here rather than before REC so the
          instruments and REC read as ONE right-aligned group.

          Left of this: the two CONTROLS, pinned left, where a hand goes.
          Right of it: everything MEASURED — state, frame size, fps, bitrate,
          sensor-to-here, and the recording state — ending flush at the right
          edge. REC was previously separated from the figures by the whole empty
          middle of the bar, which read as though it belonged to the controls; it
          does not. It is a reading, and it now sits with the readings. */}
      <span className={styles.footSpacer} />
      {/* A flagged session whose composition never started must SAY so, rather
          than leaving the operator to discover an empty Videos tab later. */}
      {video.session?.recording_error != null && (
        <span className={styles.footFact} title={video.session.recording_error}>
          recording unavailable
        </span>
      )}
      {/* The state, next to the control that changes it, so the operator is
          never guessing whether a press landed. */}
      <span className={styles.footFact} data-video-state={video.state}
            title={video.reason ?? 'live video session state'}>
        {video.state}{video.viewers !== null ? ` · ${video.viewers} watching` : ''}
      </span>
      <span className={styles.footSep} aria-hidden="true">·</span>
      {/* THE AUTHORITY SELECTOR, THE LEASE CHIP AND THE CODEC USED TO SIT HERE.
          They were costing 226px of a 469px bar, and this strip has
          `overflow: hidden` — so what they pushed off the end was not decoration
          but the MEASUREMENTS: frame size, fps, bitrate, the SEI glass-to-glass
          figure, and the REC state. 741px of content in 469px, clipped silently.

          Losing measurements to keep a disabled dropdown is exactly backwards.
          All three also had cheaper homes:

            Observer / lease   the rail's Control lease panel carries the full
                               seven-state ladder and the authority, with room
                               to explain itself. This was a duplicate.
            H.264              declared, not measured. PRD-0002 D11 pins it end
                               to end for v1, so it never changes and never
                               tells anyone anything.

          What is left here is what this bar is FOR: things that are true right
          now and are known only by measuring them. */}
      {/* MEASURED, not declared. These three were em-dash literals — the empty
          state was written and then never wired to the poller that fills it, so
          a genuinely live 640x360 stream reported `— px · — fps` beside its own
          picture. The hook had the numbers the whole time (#244).

          The dash stays the honest answer for a stat that has not arrived:
          a subscriber DIFFERENCES counters, so fps and bitrate are legitimately
          absent until the second sample, and inventing a first-sample figure is
          the defect this file names as the worst one available to it. */}
      <span
        className={styles.footFact}
        title={frameSize
          ? 'Frame size, read off the decoded frame — a remote track does not advertise its own geometry.'
          : 'Frame size — unknown until a frame has been decoded'}
      >
        {frameSize ? `${frameSize.w}\u00d7${frameSize.h}` : NO_VALUE} px
      </span>
      <span className={styles.footSep} aria-hidden="true">·</span>
      <span
        className={styles.footFact}
        title={video.stats.fps !== null
          ? `Frame rate, decoded — differenced over the sample window.${
            video.stats.packetsLost !== null ? ` ${video.stats.packetsLost} packets lost.` : ''}${
            video.stats.jitterBufferMs !== null
              ? ` Jitter buffer ${video.stats.jitterBufferMs} ms — one leg of the receive path, NOT glass-to-glass (D10).`
              : ''}`
          : 'Frame rate — unknown until a track is subscribed'}
      >
        {video.stats.fps ?? NO_VALUE} fps
      </span>
      <span className={styles.footSep} aria-hidden="true">·</span>
      <span
        className={styles.footFact}
        title={video.stats.bitrateKbps !== null
          ? `Receive bitrate, differenced over the sample window.${
            video.stats.networkQuality !== null
              ? ` Link quality ${video.stats.networkQuality}/4, straight from the SDK.`
              : ''}`
          : 'Receive bitrate — unknown until a second sample'}
      >
        {video.stats.bitrateKbps ?? NO_VALUE} kbps
      </span>
      <span className={styles.footSep} aria-hidden="true">·</span>
      {/* Capture-to-arrival, from the frame's own SEI stamp (D10, #259).
          NOT labelled glass-to-glass, and the tooltip says why: it carries the
          clock skew between robot and browser, measured at ~90 ms between two
          NTP-synchronised hosts — the same order as the figure itself. Naming
          it "sensor→here" is the honest description of what is subtracted.
          The em dash stands whenever no credible stamp has arrived. */}
      <span
        className={styles.footFact}
        title={video.sei.captureToArrivalMs !== null
          ? (video.sei.clock
            ? 'Sensor to here: this frame\u2019s own capture timestamp subtracted '
              + 'from arrival, with this browser\u2019s clock error removed '
              + `(${video.sei.clock.offsetMs > 0 ? '+' : ''}`
              + `${video.sei.clock.offsetMs} ms against the API, `
              + `\u00b1${Math.round(video.sei.clock.rttMs / 2)} ms).`
            : 'Sensor to here: this frame\u2019s own capture timestamp subtracted '
              + 'from arrival. This browser\u2019s clock could NOT be measured '
              + 'against the API, so the figure still carries whatever the two '
              + 'clocks disagree by \u2014 which has been observed at ~180 ms.')
          : video.sei.count > 0
            ? 'Sensor to here \u2014 stamps are arriving but none has been credible '
              + 'yet; an unsynchronised clock is the usual cause.'
            : 'Sensor to here \u2014 needs the robot\u2019s SEI frame stamps'}
      >
        {video.sei.captureToArrivalMs !== null
          ? `~${video.sei.captureToArrivalMs}` : NO_VALUE} ms
      </span>
      {/* THE RECORDING STATE, and it is a MEASUREMENT now rather than a
          placeholder. This said "REC —" with the tooltip "recording is out of
          scope for PRD-0010" — true when written, false since #248 shipped, and
          it kept saying the em dash through sessions that were being recorded.
          An em dash is the right answer for a figure that is unavailable, which
          is exactly what this one stopped being.

          Read from `video.session.recording`, the SAME field the Record control
          reads. Two indicators of one fact in one strip is how they end up
          disagreeing, and this bar's whole discipline is that it does not state
          anything it has not measured.

          Bottom-right of the stage, where the eye already is during a session —
          the control that CHANGES it lives at the far left of this bar, which is
          a different job. */}
      <span
        className={styles.rec}
        title={!videoOn
          ? 'Recording state — there is no session to record.'
          : video.session?.recording === true
            ? 'This session is being recorded to the cloud and kept 30 days. It is '
              + 'the CLOUD-SIDE copy: it loses frames exactly when the link does. '
              + 'The robot\u2019s own capture remains the field record.'
            : 'This session is not being recorded. Recording is fixed when a '
              + 'session opens; stop and restart to change it.'}
      >
        <StatusDot
          tone={videoOn && video.session?.recording === true ? 'warn' : 'neutral'}
          hollow={!(videoOn && video.session?.recording === true)}
        />
        {!videoOn
          ? `REC ${NO_VALUE}`
          : video.session?.recording === true ? 'REC' : 'REC off'}
      </span>
    </>
  );

  /* The camera picker's menu. Full topic paths here, because this is where an
     operator decides — the control above shows the short name because it has
     ~100px, but a choice between `/depth_cam/rgb` and `/depth_cam/depth` needs
     the path. The robot's default is marked, so "why is it on this one" has an
     answer on screen rather than in a doc. */
  const cameraMenu =
    camAnchor === null || cameras.length === 0 ? null : (
      <div
        className={styles.camMenu}
        style={{ left: camAnchor.x, top: camAnchor.y } as CSSProperties}
        role="menu"
        aria-label="Select camera"
      >
        {cameras.map((c) => {
          const on = c.topic === track;
          return (
            <button
              key={c.topic}
              type="button"
              role="menuitemradio"
              aria-checked={on}
              className={styles.menuItem}
              onClick={(e) => { e.stopPropagation(); onPickCamera(c.topic); }}
            >
              <Icon name={on ? 'check' : 'camera'} size={14} />
              {c.topic}
              <span className={styles.menuKbd}>{c.isDefault ? 'default' : ''}</span>
            </button>
          );
        })}
      </div>
    );

  const deviceMenu =
    menu === null ? null : (
      <div
        className={styles.menu}
        style={{ left: menu.x, top: menu.y } as CSSProperties}
        role="menu"
        aria-label="Device actions"
      >
        {STAGE_MENU.map((m) => {
          const off = m.id === 'request';
          return (
            <button
              key={m.id}
              type="button"
              role="menuitem"
              className={styles.menuItem}
              disabled={off}
              onClick={() => {
                setMenu(null);
                onAction(m.id);
              }}
            >
              <Icon name={m.icon} size={14} />
              {m.label}
              <span className={styles.menuKbd}>{m.kbd}</span>
            </button>
          );
        })}
      </div>
    );

  return (
    <Panel
      className={styles.stagePanel}
      viewport
      pad="none"
      title={
        <span className={styles.stageTitle}>
          Camera
          {/* The picker is built, and it is empty, because the agent
              advertises its camera inventory when the LiveKit session opens
              and the registry carries none. A disabled selector with the
              reason printed beats an invented list of `front/rear/mast`. */}
          <button
            type="button"
            className={styles.cameraSel}
            disabled={cameras.length === 0}
            aria-haspopup="menu"
            aria-expanded={camAnchor !== null}
            onClick={(e) => {
              e.stopPropagation();
              const r = e.currentTarget.getBoundingClientRect();
              onToggleCameras({ x: r.left, y: r.bottom + 4 });
            }}
            aria-label={cameras.length === 0
              ? 'Select camera \u2014 none advertised'
              : `Select camera \u2014 ${cameras.length} advertised`}
            title={cameras.length === 0
              ? 'This robot has not advertised any servable camera topic. The '
                + 'robot decides that list, not the console: it publishes only '
                + 'topics with a live publisher that fit its CPU budget.'
              : 'Cameras this robot will serve. Choosing one re-opens the '
                + 'session on it; a topic it has not advertised is refused on '
                + 'the robot.'}
          >
            {cameras.length === 0 ? (
              <>
                {/* Two lengths of the same truth, so a narrow stage sheds
                    words rather than clipping the one control on this header
                    that says what you are looking at. */}
                <span className={styles.camLong}>none advertised</span>
                <span className={styles.camShort}>none</span>
              </>
            ) : (
              <>
                <span className={styles.camLong}>{shortCamera(track)}</span>
                <span className={styles.camShort}>{shortCamera(track)}</span>
              </>
            )}
            <Icon name="chevron-down" size={12} />
          </button>
          {cameraMenu}
        </span>
      }
      tools={
        <>
          {sim && (
            <Chip
              tone="warn"
              mono
              title="meta.kind is `sim` — this robot is an Isaac Sim instance, not hardware"
            >
              Simulated
            </Chip>
          )}
          {/* THE DEAD CAMERA ICON CAME OUT HERE. `video-off`, `disabled`, with
              no handler behind it — "Request stream" was a verb the media
              plane has no endpoint for, and the control that actually makes a
              robot publish is `Start video`, 30px below in the bar. Two
              controls for one job, one of which never worked. */}
          {/* Wrapped only so the icon can go white while there is something to
              expand — IconButton sets its own className, so there is no way to
              reach it from a prop. */}
          <span className={styles.expandTool}>
            <IconButton
              icon="expand"
              label={live
                ? 'Expand the stage over the page'
                : 'Expand stage — no stream to expand'}
              size="sm"
              disabled={!live}
              onClick={(e) => {
                returnFocus.current = e.currentTarget as HTMLElement;
                setExpanded(true);
              }}
            />
          </span>
          {/* The device menu is a right-click on the strip, the way Formant
              has it. This is the same menu on a real control, so it is
              discoverable and reachable from the keyboard rather than being a
              gesture you have to already know about. */}
          <IconButton
            icon="more-vertical"
            label="Device actions"
            size="sm"
            active={menu !== null}
            onClick={(e) => {
              const box = stageRef.current?.getBoundingClientRect();
              const btn = e.currentTarget.getBoundingClientRect();
              setMenu(
                menu === null ? { x: Math.max(8, btn.right - (box?.left ?? 0) - 216), y: 8 } : null,
              );
            }}
          />
        </>
      }
    >
      <div className={styles.stageBody}>
        {/* The scrim is a sibling, not a wrapper: wrapping the stage would put
            a new element between it and its grid row and change the layout the
            collapsed state depends on. Fixed, so it covers the console. */}
        {expanded && (
          <div
            className={styles.stageScrim}
            onClick={() => setExpanded(false)}
            aria-hidden="true"
          />
        )}
        {/* THE STAGE'S SLOT, HELD OPEN. Promoting the stage to `absolute` takes
            it out of `.stageBody`'s flex column, and everything below it — the
            CPU/DISK/BAT/TEMP/PING strip and the topics band — slid up into the
            gap the moment the layer opened. Nothing about those readings
            changed, so nothing about them should move: a strip that jumps
            makes an operator re-find values they were already reading, and on
            this surface a moving number reads as a CHANGING number.

            Same `flex: 1 1 auto` the stage has, so it takes exactly the space
            the stage gave up. */}
        {expanded && <div className={styles.stagePlaceholder} aria-hidden="true" />}
        <div
          className={styles.stage}
          data-expanded={expanded}
          role={expanded ? 'dialog' : 'group'}
          aria-modal={expanded || undefined}
          ref={stageRef}
          aria-label={`Video stage — ${robot.robot_id}, ${
            track === null ? 'no session, awaiting stream' : track
          }`}
          onContextMenu={openMenu}
        >
          {/* THE WAY BACK, inside the layer — a modal whose only exit is a key
              you have to already know about is a modal you can get stuck in.
              Labelled rather than icon-only: this one is not guessable from a
              glyph, and it names the state it returns you to. Esc is shown
              beside it rather than instead of it. */}
          {expanded && (
            <div className={styles.stageLayerTools}>
              {/* STOP, in the layer. The control that ends a session lives at
                  the far left of the bar, and at this size that is the far
                  corner of a full-page picture — so the one verb an operator
                  might want in a hurry was the furthest thing from their
                  cursor. Same `video.stop()` the bar calls, not a second path.

                  Stopping also CLOSES this layer, and does it through the
                  state rather than by chaining the two here: the picture goes
                  away, `live` goes false, and the collapse effect above takes
                  it down. That way a session that ends any other way — the
                  robot dropping, another operator stopping it, the reaper —
                  closes the layer too, instead of leaving one route tidy and
                  the rest of them holding a black rectangle. */}
              <button
                type="button"
                className={styles.stageStop}
                onClick={() => video.stop()}
                title="Stop watching. The stream ends when the last watcher leaves — and this view closes with it."
              >
                <Icon name="video-off" size={12} />
                Stop video
              </button>
              <button
                type="button"
                ref={exitRef}
                className={styles.stageExit}
                onClick={() => setExpanded(false)}
              >
                <Icon name="collapse" size={12} />
                Exit full width
                <span className={styles.stageExitKbd}>Esc</span>
              </button>
            </div>
          )}
          <div className={styles.pictureWrap}>
            {/* THE FRAME. --void at the stream's aspect, with its own hairline
                edge so the reserved rectangle is legible against a letterbox
                that is the same black. */}
            <div
              className={styles.picture}
              style={{ '--stream-ar': measuredAr ?? STREAM_AR.css } as CSSProperties}
              data-live={live}
              data-session={video.session !== null}
            >
              {video.stream !== null && (
                <video
                  ref={videoRef}
                  className={styles.video}
                  autoPlay
                  playsInline
                  muted
                  aria-label={`Live video from ${robot.robot_id}`}
                />
              )}
              {/* THE HEADING AND SPEED TAPES CAME OFF THE PICTURE HERE.

                  They were the HUD — heading across the top, speed down the
                  leading edge, both stroke-only over the image. The indicator
                  that said whether they were live or parked on a scrubbed
                  instant went first; these are the readings it described.

                  Neither is video. Both are telemetry, and both are still on
                  this screen where telemetry belongs: heading and speed are in
                  the rail's drive block, at the playhead, with their units and
                  their age. Drawing them a second time over the picture bought
                  a glance and cost the operator the only thing this pane is
                  for — an unobstructed view of what the robot can see. */}
              {!live && (
                <div className={styles.noSignal}>
                  {video.reason ?? honest}
                </div>
              )}
              {/* The rectangle's shape is a claim, so it names its own
                  provenance: nothing has advertised a track, so the aspect is
                  the one PRD-0002 D11 pins, and it says so. */}
              <span
                className={styles.arNote}
                title={measuredAr
                  ? 'Drawn at the resolution the subscribed track advertises.'
                  : STREAM_AR.why}
              >
                {frameSize
                  ? `${frameSize.w}\u00d7${frameSize.h}`
                  : STREAM_AR.label}
              </span>
            </div>
          </div>
          {/* The bar beneath the frame. Control authority at the stage's
              bottom-left, exactly where skydio puts `Manual ▾`. */}
          <div className={styles.stageFoot}>{streamFacts}</div>
          {deviceMenu}
        </div>

        {/* ---- vitals strip, along the bottom of the stage ----------------
                formant-teleop-ground-station.png, at a third of its height:
                28px rings on a 3px stroke, the track a fixed neutral, and the
                arc coloured ONLY on the gauge that has breached its published
                limit (tell 28). The limit itself is a 1px tick on the track,
                so the threshold is on the screen, not in the operator's head. */}
        <div
          className={styles.vitals}
          data-stale={stale}
          role="group"
          aria-label={stale ? `Robot vitals — stale, newest reading ${sample.age} old` : 'Robot vitals'}
        >
          {/* The age rides INSIDE the strip, ahead of the first ring, so no
              vital on this bar can be read without its age having been read
              first. It is the only saturated thing here when the sample is
              old, and the rings behind it are all at 40% — the two together
              make a stale strip impossible to mistake for a live one from
              across the room, which is the entire failure mode §11.10 exists
              to name. Four telemetry rings; PING is the control channel and
              carries its own (absent) freshness, not the telemetry stream's. */}
          {stale && r !== undefined && (
            <span
              className={styles.vitalsAge}
              title={`These four readings are the newest this robot has sent, and they are ${sample.age} old. Nothing on this strip is a measurement of now.`}
            >
              <span className={styles.vitalsAgeTick} aria-hidden="true" />
              <span className={styles.vitalsAgeLabel}>AGE</span>
              {sample.age}
            </span>
          )}
          {r === undefined && (
            <span
              className={styles.vitalsAge}
              data-void="true"
              title="The playhead is inside a coverage hole — this robot sent nothing at or near this instant, so there is no reading to render."
            >
              <span className={styles.vitalsAgeTick} aria-hidden="true" />
              <span className={styles.vitalsAgeLabel}>GAP</span>
              no data
            </span>
          )}
          <VitalRing label="CPU" value={cpu} unit="%" warn={75} bad={90} invert stale={stale} age={sample.age} />
          <VitalRing label="DISK" value={disk} unit="%" warn={80} bad={92} invert stale={stale} age={sample.age} />
          <VitalRing label="BAT" value={bat} unit="%" warn={30} bad={15} stale={stale} age={sample.age} />
          <VitalRing
            label="TEMP"
            value={temp}
            unit="°C"
            min={0}
            max={90}
            warn={65}
            bad={80}
            invert
            stale={stale}
            age={sample.age}
          />
          <VitalRing
            label="PING"
            value={null}
            unit="ms"
            min={0}
            max={500}
            warn={150}
            bad={400}
            invert
            note="no session — the control channel has never opened"
            onClick={onOpenLink}
            open={linkOpen}
            shortcut="⇧K"
          />
          <span className={styles.vitalsSpacer} />
        </div>

        {/* ---- named boolean topics, and the authority they qualify -------
                One 22px band: the robot's own booleans on the left, the
                control authority this operator holds on the right. Formant
                ends its vitals bar with exactly this — literal topic paths in
                uppercase mono, true/false/never as three distinct renderings
                — and skydio-remote-flight-deck-live.png puts the mode
                selector at the stage's edge because control authority is the
                most consequential fact on the screen. */}
        <div className={styles.topics} role="group" aria-label="Boolean topics and control authority">
          <TopicDot
            topic="/AGENT_LINK"
            value={frame.replay ? null : robot.liveness === 'live'}
            title={
              frame.replay
                ? 'Registry liveness is a fact about now, and the playhead is not at now — the console does not have a history of it, so it renders as never-published rather than being back-dated.'
                : `Derived from registry liveness — currently ${robot.liveness}`
            }
          />
          {/* A satellite count from a sample this old is not a claim about
              the fix the robot has NOW, so it renders as the third state —
              hollow, 40% — rather than asserting `true`. */}
          <TopicDot
            topic="/GNSS_FIX"
            value={sats === null || stale ? null : sats >= 4}
            title={
              sats === null
                ? 'gnss_sats has never appeared on this robot’s telemetry payload at this instant'
                : stale
                  ? `${sats} satellites on the nearest reading, which is ${sample.age} old — too old to assert a fix`
                  : `${sats} satellites on the reading at this instant — fix asserted at 4`
            }
          />
          <TopicDot
            topic="/MOTION_OK"
            value={frame.replay ? null : motionOk}
            title={
              frame.replay
                ? 'Registry status is a fact about now; the console holds no history of it, so it is not back-dated onto a scrubbed instant.'
                : `Registry status is ${robot.status}; a faulted or held robot refuses motion commands`
            }
          />
          <TopicDot
            topic="/VIDEO_TRACK"
            value={null}
            title="Never published — the WebRTC media plane is not built (PRD-0002 Phase A)"
          />
          <TopicDot
            topic="/CTRL_CHAN"
            value={null}
            title="Never published — the lossy control data channel is Phase B, gated on the robot-side safe-stop sign-off"
          />
        </div>
      </div>
    </Panel>
  );
}

/* ==========================================================================
   LINK HEALTH — the drill-down behind the PING chip

   THE BAR §11.3 is explicit about the shape: one summary chip in the vitals
   strip expands into a single card carrying the complete field list —
   counters, loss, central tendency, spread, both bitrates, both endpoints,
   and an ABSOLUTE Zulu stamp for the last successful round trip.

   Round 2 shipped this as a permanent 1150x130 five-column panel. That was
   wrong twice over: it spent ~150,000px of the page on twenty-five fields of
   which fifteen can never be populated, and at 1280 the flexed columns
   collapsed two whole groups to zero width with no fallback. A fixed-width
   card fixes both — it cannot shed a column, because it has no elastic ones,
   and it costs the page nothing when it is shut.

   Every WebRTC field is an em dash, and the card says why at the top. The
   alternative — a plausible `38 ms` — is the single worst string this file
   could print (§11.10). Beside those dashes sit the two round trips this
   platform genuinely measures, clearly labelled as what they are; the gap
   between the left column and the right column is the entire argument for
   building PRD-0002.
   ========================================================================== */

interface LoopField {
  label: string;
  value: string;
  missing?: boolean;
  title?: string;
}

const DASH: (label: string, title?: string) => LoopField = (label, title) => ({
  label,
  value: NO_VALUE,
  missing: true,
  ...(title === undefined ? {} : { title }),
});

function LoopGroup({
  head,
  note,
  fields,
}: {
  head: string;
  note: string;
  fields: readonly LoopField[];
}) {
  return (
    <div className={styles.loopGroup}>
      <div className={styles.loopHead}>
        {head}
        <span className={styles.loopHeadNote}>{note}</span>
      </div>
      <dl className={styles.loopRows}>
        {fields.map((f) => (
          <div key={f.label} className={styles.loopRow} title={f.title}>
            <dt>{f.label}</dt>
            <dd data-missing={f.missing === true}>{f.value}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

function LinkHealth({
  rtt,
  latest,
  cadenceMs,
  onClose,
}: {
  rtt: Rtt;
  latest: TelemetryReading | undefined;
  cadenceMs: number | null;
  onClose: () => void;
}) {
  const rssi = payloadNum(latest, 'link_rssi_dbm');
  const sats = payloadNum(latest, 'gnss_sats');
  const offset =
    latest === undefined
      ? null
      : (() => {
          const v = (latest as unknown as Record<string, unknown>)['clock_offset_ms'];
          return typeof v === 'number' && !Number.isNaN(v) ? v : null;
        })();

  return (
    <div className={styles.linkCard} role="dialog" aria-label="Link health">
      <div className={styles.linkHead}>
        <Eyebrow>Link health</Eyebrow>
        <span className={styles.linkHeadSpacer} />
        <span className={styles.linkHeadNote}>PING is the control channel, not telemetry</span>
        <IconButton icon="close" label="Close link health (⇧K)" size="sm" onClick={onClose} />
      </div>
      <div className={styles.linkGrid}>
        <section className={styles.loopCol}>
          <LoopGroup
            head="Control channel"
            note="webrtc/lossy"
            fields={[
              DASH('Pings'),
              DASH('Pongs'),
              DASH('Loss'),
              DASH('Average'),
              DASH('Min'),
              DASH('Max'),
              DASH('Jitter σ'),
              DASH('TX to device'),
              DASH('RX from device'),
              DASH('Local IP'),
              DASH('Device IP'),
              DASH('P2P'),
              DASH(
                'Last pong',
                'Absolute and Zulu when it exists. A relative age is the only thing still ticking when a link dies, and it looks alive.',
              ),
            ]}
          />
        </section>
        <section className={styles.loopCol} data-measured="true">
          <LoopGroup
            head="Dispatch queue"
            note="https/poll"
            fields={[
              { label: 'Samples', value: rtt.n === 0 ? NO_VALUE : String(rtt.n), missing: rtt.n === 0 },
              {
                label: 'Median',
                value: rtt.medianMs === null ? NO_VALUE : duration(rtt.medianMs),
                missing: rtt.medianMs === null,
                title: 'issue → first transition, over /api/audit/commands',
              },
              {
                label: 'Min',
                value: rtt.minMs === null ? NO_VALUE : duration(rtt.minMs),
                missing: rtt.minMs === null,
              },
              {
                label: 'Max',
                value: rtt.maxMs === null ? NO_VALUE : duration(rtt.maxMs),
                missing: rtt.maxMs === null,
              },
              {
                label: 'Last ack',
                value: rtt.lastAt === null ? NO_VALUE : utc(rtt.lastAt).slice(11),
                missing: rtt.lastAt === null,
                title:
                  'The last successful dispatch acknowledgement. Absolute and Zulu — when a link dies a relative age is the only thing still ticking, and it looks alive.',
              },
            ]}
          />
          <LoopGroup
            head="Robot uplink"
            note="telemetry"
            fields={[
              {
                label: 'RSSI',
                value: rssi === null ? NO_VALUE : `${rssi.toFixed(0)} dBm`,
                missing: rssi === null,
                title: "The robot's own radio, not this browser's path to it.",
              },
              {
                label: 'GNSS sats',
                value: sats === null ? NO_VALUE : sats.toFixed(0),
                missing: sats === null,
              },
              {
                label: 'Cadence',
                value: cadenceMs === null ? NO_VALUE : duration(cadenceMs),
                missing: cadenceMs === null,
              },
              {
                label: 'Clock skew',
                value: offset === null ? NO_VALUE : `${offset.toFixed(1)} ms`,
                missing: offset === null,
                title: "The agent's clock against the server's (clock_offset_ms).",
              },
              {
                label: 'Sample',
                value: latest === undefined ? NO_VALUE : utc(latest.ts).slice(11),
                missing: latest === undefined,
              },
            ]}
          />
        </section>
      </div>
      <div className={styles.loopFoot}>
        Left · the loop PRD-0002 specifies, never opened. Right · the loop that exists —{' '}
        <b>{rtt.medianMs === null ? NO_VALUE : duration(rtt.medianMs)}</b> median, n={rtt.n}.
      </div>
    </div>
  );
}

/* ==========================================================================
   STAGED DISPATCH — direct manipulation stages, a button commits.

   THE BAR §11.9 / tell 31: nothing is ever sent to a robot on drag-release, on
   click, or on blur. Clicking the map STAGES a `move` waypoint; the inspector
   exposes the same geometry as raw editable numbers bound to the marker; and
   nothing leaves the browser until a pinned CANCEL / SEND pair of equal size
   is pressed. `move` is a real command in the morphology's profile with a
   real required `waypoint` param, so the commit is a genuine write.
   ========================================================================== */

interface Staged {
  lat: number;
  lon: number;
}

const R_EARTH = 6371000;

function rangeM(a: Staged, b: Staged): number {
  const dLat = ((b.lat - a.lat) * Math.PI) / 180;
  const dLon = ((b.lon - a.lon) * Math.PI) / 180;
  const la1 = (a.lat * Math.PI) / 180;
  const la2 = (b.lat * Math.PI) / 180;
  const h =
    Math.sin(dLat / 2) ** 2 + Math.cos(la1) * Math.cos(la2) * Math.sin(dLon / 2) ** 2;
  return 2 * R_EARTH * Math.asin(Math.min(1, Math.sqrt(h)));
}

function bearingDeg(a: Staged, b: Staged): number {
  const la1 = (a.lat * Math.PI) / 180;
  const la2 = (b.lat * Math.PI) / 180;
  const dLon = ((b.lon - a.lon) * Math.PI) / 180;
  const y = Math.sin(dLon) * Math.cos(la2);
  const x = Math.cos(la1) * Math.sin(la2) - Math.sin(la1) * Math.cos(la2) * Math.cos(dLon);
  return (((Math.atan2(y, x) * 180) / Math.PI) + 360) % 360;
}

function MoveInspector({
  robot,
  staged,
  hazard,
  pending,
  onChange,
  onCancel,
  onSend,
}: {
  robot: Robot;
  staged: Staged;
  hazard: string;
  pending: boolean;
  onChange: (next: Staged) => void;
  onCancel: () => void;
  onSend: () => void;
}) {
  const from =
    robot.lat == null || robot.lon == null ? null : { lat: robot.lat, lon: robot.lon };
  return (
    <div className={styles.inspector} role="group" aria-label="Staged move">
      <div className={styles.inspectorHead}>
        <Eyebrow>Staged move</Eyebrow>
        <span className={styles.inspectorSpacer} />
        <span className={styles.inspectorHazard} data-tone={HAZARD_TONE[hazard] ?? 'neutral'}>
          {hazard}
        </span>
      </div>
      <label className={styles.inspectorRow}>
        <span>Latitude</span>
        <Input
          size="sm"
          mono
          type="number"
          step="0.00001"
          defaultValue={staged.lat.toFixed(5)}
          onChange={(e) => {
            const v = Number(e.currentTarget.value);
            if (Number.isFinite(v)) onChange({ ...staged, lat: v });
          }}
        />
      </label>
      <label className={styles.inspectorRow}>
        <span>Longitude</span>
        <Input
          size="sm"
          mono
          type="number"
          step="0.00001"
          defaultValue={staged.lon.toFixed(5)}
          onChange={(e) => {
            const v = Number(e.currentTarget.value);
            if (Number.isFinite(v)) onChange({ ...staged, lon: v });
          }}
        />
      </label>
      <dl className={styles.inspectorFacts}>
        <div>
          <dt>Bearing</dt>
          <dd>
            {from === null ? NO_VALUE : bearingDeg(from, staged).toFixed(0)}
            <span>{from === null ? '' : '° N'}</span>
          </dd>
        </div>
        <div>
          <dt>Range</dt>
          <dd>
            {from === null ? NO_VALUE : rangeM(from, staged).toFixed(0)}
            <span>{from === null ? '' : ' m'}</span>
          </dd>
        </div>
      </dl>
      <div className={styles.commit}>
        <Button variant="secondary" size="sm" onClick={onCancel} disabled={pending}>
          Cancel
        </Button>
        <button type="button" className={styles.commitVerb} onClick={onSend} disabled={pending}>
          Send move
        </button>
      </div>
      <div className={styles.inspectorNote}>
        staged only — nothing has been sent to {robot.robot_id}
      </div>
    </div>
  );
}

/* ==========================================================================
   THE MAP
   ========================================================================== */

const TILE_URL =
  'https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Dark_Gray_Base/MapServer/tile/{z}/{y}/{x}';
const TILE_MAX_NATIVE = 16;
const BASEMAP = 'Esri Dark Gray';

/* What the map draws, beyond the pose and the track. Each of these is a real
   record from the API — a capture session's own reported bounding box, a
   `move` command's own waypoint param, a robot's own registry fix. Nothing
   here is a synthesised site plan: the platform carries no block/row geometry
   and no geofence, so none is drawn (see the report accompanying this file). */
interface Coverage {
  id: number;
  session: string;
  state: ProcessingState;
  started: string | null;
  size: number;
  bounds: L.LatLngBounds;
}

function coverageOf(scans: readonly Scan[] | undefined): Coverage[] {
  if (scans === undefined) return [];
  const out: Coverage[] = [];
  for (const s of scans) {
    const { bbox_min_lat: a, bbox_min_lon: b, bbox_max_lat: c, bbox_max_lon: d } = s as Scan & {
      bbox_min_lat?: number | null;
      bbox_min_lon?: number | null;
      bbox_max_lat?: number | null;
      bbox_max_lon?: number | null;
    };
    if (typeof a !== 'number' || typeof b !== 'number' || typeof c !== 'number' || typeof d !== 'number') {
      continue;
    }
    out.push({
      id: s.id,
      /* `20260828T1106Z_row-14` → `row-14`. The operator names the block, not
         the timestamp; the timestamp is the tooltip. */
      session: s.session_id.includes('_') ? s.session_id.slice(s.session_id.indexOf('_') + 1) : s.session_id,
      state: s.processing_state,
      started: s.started_utc,
      size: s.size_bytes,
      bounds: L.latLngBounds([a, b], [c, d]),
    });
  }
  return out;
}

const COVERAGE_TONE: Record<string, string> = {
  copc: 'ok',
  processing: 'active',
  raw: 'idle',
  failed: 'bad',
};

/** A dispatched `move`, as the geometry it actually carried. */
interface Planned {
  id: number;
  lat: number;
  lon: number;
  status: CommandStatus;
  issuedAt: string;
}

function plannedOf(commands: readonly Command[]): Planned[] {
  const out: Planned[] = [];
  for (const c of commands) {
    if (c.command !== 'move') continue;
    const w = c.params['waypoint'];
    if (w === null || typeof w !== 'object') continue;
    const lat = (w as Record<string, unknown>)['lat'];
    const lon = (w as Record<string, unknown>)['lon'];
    if (typeof lat !== 'number' || typeof lon !== 'number') continue;
    if (c.status === 'failed' || c.status === 'cancelled') continue;
    out.push({ id: c.id, lat, lon, status: c.status, issuedAt: c.issued_at });
  }
  return out.sort((a, b) => Date.parse(a.issuedAt) - Date.parse(b.issuedAt));
}

type LayerKey = 'track' | 'coverage' | 'planned' | 'fleet';

function TeleopMap({
  robot,
  readings,
  fixAgeS,
  frame,
  coverage,
  focus,
  siblings,
  planned,
  staged,
  canStage,
  stageBlocked,
  onStage,
  onGoLive,
  children,
}: {
  robot: Robot;
  readings: readonly TelemetryReading[];
  fixAgeS: number | null;
  /** The instant this map is drawing — see frameAt(). */
  frame: Frame;
  coverage: readonly Coverage[];
  /** A capture session the operator asked to look at, from the rail. */
  focus: Coverage | null;
  siblings: readonly Robot[];
  planned: readonly Planned[];
  staged: Staged | null;
  canStage: boolean;
  stageBlocked: 'verb' | 'fix' | null;
  onStage: (next: Staged) => void;
  onGoLive: () => void;
  children?: ReactNode;
}) {
  const hostRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<L.Map | null>(null);
  const layerRef = useRef<L.LayerGroup | null>(null);
  const framed = useRef(false);
  const stageRef = useRef({ can: canStage, fn: onStage });
  stageRef.current = { can: canStage, fn: onStage };
  const [tiles, setTiles] = useState<'pending' | 'ok' | 'unavailable'>('pending');
  const [follow, setFollow] = useState(true);
  /* The hour marks below are thinned in PIXEL space, so the thinning has to be
     recomputed when the scale changes. Bumped on zoomend only — a user act,
     not a stream. */
  const [zoomGen, setZoomGen] = useState(0);
  /* palantir-gotham-map-workspace.png's numbered layer tree with per-node
     visibility, at the size a 4-layer map deserves. Everything is on, because
     everything on it is a real record; turning one off is the operator's
     choice, not a default that hides data. */
  const [on, setOn] = useState<Record<LayerKey, boolean>>({
    track: true,
    coverage: true,
    planned: true,
    fleet: true,
  });

  /* The capture footprints arrive on their own query, after the first draw.
     Framing is a once-per-mount act, so it has to be re-armed when the ground
     the operator should be looking at actually changes — otherwise the map
     stays framed on a 60 m track while four 700 m footprints sit off-screen. */
  useEffect(() => {
    framed.current = false;
  }, [coverage.length]);

  /* An explicit ask from the rail: frame this one session. It is a camera
     move the operator initiated by name, which is the only kind THE BAR
     allows without a named follow mode. */
  useEffect(() => {
    const map = mapRef.current;
    if (map === null || focus === null) return;
    framed.current = true;
    map.fitBounds(focus.bounds, { padding: [30, 30], animate: false, maxZoom: 19 });
  }, [focus]);

  useEffect(() => {
    const host = hostRef.current;
    if (host === null) return;
    const map = L.map(host, {
      center: [0, 0],
      zoom: 17,
      minZoom: 2,
      maxZoom: 20,
      zoomControl: false,
      attributionControl: false,
      zoomAnimation: false,
      fadeAnimation: false,
      markerZoomAnimation: false,
      zoomSnap: 0.25,
      zoomDelta: 1,
    });
    const base = L.tileLayer(TILE_URL, {
      maxZoom: 20,
      maxNativeZoom: TILE_MAX_NATIVE,
      className: 'f3-teleop-tile',
    });
    let loaded = 0;
    const started = Date.now();
    base.on('tileload', () => {
      loaded += 1;
      setTiles('ok');
    });
    base.on('tileerror', () => {
      if (loaded === 0 && Date.now() - started > 4000) setTiles('unavailable');
    });
    const grace = window.setTimeout(() => {
      if (loaded === 0) setTiles('unavailable');
    }, 12_000);
    base.addTo(map);
    /* Map corner furniture, bottom-right and stroke-only (THE BAR §5). A
       teleop map is the surface the operator stages a waypoint on, so "how
       far is that" has to be answerable without arithmetic; the inspector
       prints the range in metres and this prints the ruler it is against.
       Metric only — every distance in this console is metres. */
    L.control.scale({ imperial: false, metric: true, position: 'bottomright', maxWidth: 120 }).addTo(map);
    layerRef.current = L.layerGroup().addTo(map);
    map.on('click', (e: L.LeafletMouseEvent) => {
      if (!stageRef.current.can) return;
      stageRef.current.fn({ lat: e.latlng.lat, lon: e.latlng.lng });
    });
    map.on('zoomend', () => setZoomGen((g) => g + 1));
    mapRef.current = map;
    const ro = new ResizeObserver(() => map.invalidateSize({ animate: false }));
    ro.observe(host);
    return () => {
      window.clearTimeout(grace);
      ro.disconnect();
      map.remove();
      mapRef.current = null;
      layerRef.current = null;
    };
  }, []);

  /* The pose's own freshness, derived from the SAME threshold as the strip's
     (sampleOf / STALE_AFTER_S) so the map and the vitals strip can never
     disagree about whether this robot is talking. THE BAR §11.10: "the map
     still draws the last known pose, GREYED, with an explicit age" — an
     11-hour-old fix rendered in the live accent hue is the same lie as a
     fabricated latency figure, told with a triangle instead of a number. */
  /* THREE renderings of a pose, because there are three different facts:

       live      the robot is here NOW           — accent halo + arrow
       stale     this is where it was last seen  — --unknown + a printed age
       replay    this is where it was at 13:39   — neutral + a Zulu stamp

     Round 4 shipped the first two (and that fixed the worst defect this file
     has had). The third arrives with the transport: a pose the operator
     scrubbed to is neither a live claim nor a stale one — it is a RECORD, and
     it has to be told apart from both. */
  const rShown = shownReading(frame);
  const pose =
    frame.replay
      ? rShown?.lat != null && rShown.lon != null
        ? { lat: rShown.lat, lon: rShown.lon, heading: rShown.heading_deg, at: rShown.ts }
        : null
      : robot.lat != null && robot.lon != null
        ? { lat: robot.lat, lon: robot.lon, heading: robot.heading_deg, at: null }
        : null;
  /* Primitives, not the object: the draw effect tears down and rebuilds every
     layer it owns, and an object literal in its dependency list would do that
     on every render rather than when the pose actually moves. */
  const poseLat = pose?.lat ?? null;
  const poseLon = pose?.lon ?? null;
  const poseHeading = pose?.heading ?? null;
  const staleFix = !frame.replay && isStale(fixAgeS);
  /* Empty until the pose is actually stale. The whole layer is torn down and
     redrawn whenever this changes, and a live robot's age string ticks once a
     SECOND — putting it in the dependency list unconditionally would rebuild
     240 markers every tick, which is both a waste and the kind of continuous
     redraw THE BAR §7 reserves for things that are genuinely moving. Past the
     threshold the string only changes once a minute. */
  const poseAge = staleFix && fixAgeS !== null
    ? duration(fixAgeS * 1000).replace(/\s+/gu, '')
    : '';

  /* Draw everything the platform actually knows about this ground. Leaflet
     owns the reprojection; this effect owns only what is true. The camera
     frustum (THE BAR §11.8) is deliberately NOT drawn: a coverage polygon is
     a live claim about what the operator can SEE, and with no stream and no
     gimbal pose it would be a lie. The capture footprints below are the
     opposite kind of claim — a finished, recorded, server-reported extent. */
  useEffect(() => {
    const map = mapRef.current;
    const layer = layerRef.current;
    if (map === null || layer === null) return;
    layer.clearLayers();

    /* `readings` is newest-first. The track is drawn oldest-first, and in
       replay it is CUT at the playhead: everything the robot had driven by
       13:39:11Z is solid, everything after it is a hairline. The track head
       is therefore the pose marker, always — scrubbing back moves the head
       back, which is the whole point of binding the map to the axis. */
    const withFix = readings.filter((r) => r.lat != null && r.lon != null);
    const before = withFix.filter((r) => !frame.replay || Date.parse(r.ts) <= frame.t);
    const after = frame.replay ? withFix.filter((r) => Date.parse(r.ts) > frame.t) : [];
    const path = before
      .slice()
      .reverse()
      .map((r) => L.latLng(r.lat as number, r.lon as number));
    const ahead = after
      .slice()
      .reverse()
      .map((r) => L.latLng(r.lat as number, r.lon as number));

    /* `f3-stale` is applied to the track as well as to the pose. The track is
       a true record and stays drawn, but when its head has expired it must not
       out-shout the marker it leads to: a bright blue trail under a grey
       arrow reads, from two metres, as a robot that is still moving. */
    const mark = frame.replay ? ' f3-replay' : staleFix ? ' f3-stale' : '';

    /* ---- 1. capture coverage, underneath everything --------------------
       The swathe of ground this robot has actually recorded, from each
       session's own reported bbox. Stroke-only at 1px with a ≤10% fill, per
       THE BAR §5, and toned by the session's processing state so "captured
       and landed" is distinguishable from "still in the COPC worker". */
    if (on.coverage) {
      for (const c of coverage) {
        L.rectangle(c.bounds, {
          className: `f3-teleop-cov f3-cov-${COVERAGE_TONE[c.state ?? 'raw'] ?? 'idle'}`,
          interactive: true,
        })
          .bindTooltip(
            `${c.session} · ${c.state ?? 'unknown'}` +
              (c.started === null ? '' : ` · ${utc(c.started)}`),
            { direction: 'top', className: 'f3-teleop-tip' },
          )
          .addTo(layer);
        L.tooltip({
          permanent: true,
          direction: 'right',
          offset: L.point(4, 0),
          className: 'f3-teleop-covlabel',
          interactive: false,
        })
          .setLatLng(L.latLng(c.bounds.getNorth(), c.bounds.getWest()))
          .setContent(c.session)
          .addTo(layer);
      }
    }

    /* ---- 2. the rest of the fleet on this ground ------------------------ */
    if (on.fleet) {
      for (const s of siblings) {
        if (s.lat == null || s.lon == null) continue;
        const here = L.latLng(s.lat, s.lon);
        L.circleMarker(here, {
          radius: 3.5,
          className: `f3-teleop-peer f3-peer-${s.liveness}`,
          interactive: true,
        })
          .bindTooltip(`${s.robot_id} · ${s.status} · ${s.liveness}`, {
            direction: 'top',
            className: 'f3-teleop-tip',
          })
          .addTo(layer);
      }
    }

    /* ---- 3. the track --------------------------------------------------- */
    if (on.track) {
      /* What happened AFTER the instant on screen, drawn as a hairline so it
         is legible as "not yet" rather than as part of the driven path. It is
         real recorded geometry, so it is never hidden — a replay that deletes
         the future is a replay you cannot navigate. */
      if (ahead.length > 0) {
        const link = path.length > 0 ? [path[path.length - 1] as L.LatLng, ...ahead] : ahead;
        if (link.length > 1) {
          L.polyline(link, { className: 'f3-teleop-ahead', interactive: false }).addTo(layer);
        }
      }
      if (path.length > 1) {
        L.polyline(path, { className: `f3-teleop-track${mark}`, interactive: false }).addTo(layer);
      }
      for (const p of path.slice(0, -1)) {
        L.circleMarker(p, {
          radius: 1.5,
          className: `f3-teleop-fix${mark}`,
          interactive: false,
        }).addTo(layer);
      }

      /* ---- hour marks on the track ---------------------------------------
         The map and the transport share one axis, so the track has to be
         readable AS time: a tick and a mono `14:00` wherever the track
         crosses a UTC hour boundary. It is what makes "scrub to 13:49" and
         "the robot was over there" the same question — the operator reads
         the answer off the ground instead of translating it.

         Thinned in pixel space so a parked robot does not pile eight labels
         on one dot, and re-thinned on zoomend for the same reason. */
      const ordered = before.slice().reverse();
      const hourMarks: { at: L.LatLng; label: string }[] = [];
      let lastHour = -1;
      for (const rd of ordered) {
        const t = Date.parse(rd.ts);
        if (!Number.isFinite(t)) continue;
        const hr = Math.floor(t / 3_600_000);
        if (lastHour !== -1 && hr !== lastHour) {
          hourMarks.push({
            at: L.latLng(rd.lat as number, rd.lon as number),
            label: axisClock(hr * 3_600_000).slice(0, 5),
          });
        }
        lastHour = hr;
      }
      /* The pose chip owns the head of the track; an hour mark under it is
         two labels on one dot. */
      const headPt =
        poseLat === null || poseLon === null
          ? null
          : map.latLngToLayerPoint(L.latLng(poseLat, poseLon));
      let lastPt: L.Point | null = null;
      for (const hm of hourMarks) {
        const pt = map.latLngToLayerPoint(hm.at);
        if (headPt !== null && pt.distanceTo(headPt) < 46) continue;
        if (lastPt !== null && pt.distanceTo(lastPt) < 44) continue;
        lastPt = pt;
        L.circleMarker(hm.at, {
          radius: 2.5,
          className: `f3-teleop-hour${mark}`,
          interactive: false,
        }).addTo(layer);
        L.tooltip({
          permanent: true,
          direction: 'left',
          offset: L.point(-4, 0),
          className: 'f3-teleop-hourlabel',
          interactive: false,
        })
          .setLatLng(hm.at)
          .setContent(hm.label)
          .addTo(layer);
      }
    }

    /* ---- 4. the planned path — dispatched `move` waypoints -------------- */
    if (on.planned && planned.length > 0) {
      const pts = planned.map((p) => L.latLng(p.lat, p.lon));
      if (pts.length > 1) {
        L.polyline(pts, {
          className: 'f3-teleop-plan',
          dashArray: '6 4',
          interactive: false,
        }).addTo(layer);
      }
      for (const p of planned) {
        L.circleMarker(L.latLng(p.lat, p.lon), {
          radius: 4,
          className: `f3-teleop-plannode f3-plan-${p.status}`,
          interactive: true,
        })
          .bindTooltip(`move ${p.id} · ${p.status} · ${utc(p.issuedAt)}`, {
            direction: 'top',
            className: 'f3-teleop-tip',
          })
          .addTo(layer);
      }
    }

    if (poseLat !== null && poseLon !== null) {
      const here = L.latLng(poseLat, poseLon);
      const deg = poseHeading ?? 0;
      const known = poseHeading != null;

      /* ---- 5. range rings, only while staging -------------------------
         Derived geometry, not data: the ruler the operator is measuring the
         staged leg against. They appear with the waypoint and leave with it,
         so the map is never carrying furniture it is not being asked for. */
      if (staged !== null) {
        const legM = rangeM({ lat: poseLat, lon: poseLon }, staged);
        /* A ruler has to be readable at the zoom the operator is actually at,
           so the step is chosen to bracket the staged leg rather than fixed in
           code: three rings, the outermost past the waypoint. */
        const step =
          [5, 10, 25, 50, 100, 250, 500, 1000, 2500].find((s) => s * 3 >= legM) ?? 5000;
        for (let i = 1; i <= 3; i += 1) {
          L.circle(here, {
            radius: step * i,
            className: 'f3-teleop-ring',
            interactive: false,
          }).addTo(layer);
          L.tooltip({
            permanent: true,
            direction: 'center',
            className: 'f3-teleop-ringlabel',
            interactive: false,
          })
            .setLatLng(L.latLng(here.lat + (step * i) / 111_320, here.lng))
            .setContent(`${step * i} m`)
            .addTo(layer);
        }
      }

      /* `f3-stale` swaps the accent for --unknown on both the halo and the
         arrow. It is a class on the marker itself rather than a state on the
         panel because Leaflet re-mounts this node on every draw, and the pose
         must never inherit "live" from a parent that happens not to have
         re-rendered. */
      const icon = L.divIcon({
        className: 'f3-teleop-marker',
        iconSize: [30, 30],
        iconAnchor: [15, 15],
        html:
          `<svg viewBox="0 0 30 30" width="30" height="30">` +
          `<circle cx="15" cy="15" r="9" class="f3-teleop-halo${mark}"/>` +
          (known
            ? `<g transform="rotate(${deg.toFixed(1)} 15 15)">` +
              `<path d="M15,3 L20,17 L15,14 L10,17 Z" class="f3-teleop-arrow${mark}"/></g>`
            : `<circle cx="15" cy="15" r="3.5" class="f3-teleop-arrow${mark}"/>`) +
          `</svg>`,
      });
      L.marker(here, { icon, interactive: false, keyboard: false }).addTo(layer);
      /* The anchored chip carries the age when the fix is stale, so the pose
         cannot be read without it — the same discipline the vitals strip runs
         (§11.10: "greyed, with an explicit age"). */
      L.tooltip({
        permanent: true,
        direction: 'bottom',
        offset: L.point(0, 12),
        className: `f3-teleop-label${mark}`,
        interactive: false,
      })
        .setLatLng(here)
        .setContent(
          frame.replay
            ? `${robot.robot_id} <span class="f3-teleop-label-age">${axisClock(frame.t)}</span>`
            : staleFix
              ? `${robot.robot_id} <span class="f3-teleop-label-age">${poseAge}</span>`
              : robot.robot_id,
        )
        .addTo(layer);

      /* Frame the ground this robot has actually worked — its track AND the
         extent of every capture session it has landed — once, so the operator
         opens on the site rather than on an arbitrary zoom level. After that
         the camera only follows, and only when the robot leaves the middle of
         the view, so following never fights the frame. */
      if (!framed.current) {
        framed.current = true;
        let b: L.LatLngBounds | null = path.length > 1 ? L.latLngBounds(path) : null;
        for (const c of coverage) b = b === null ? c.bounds : b.extend(c.bounds);
        if (b === null) map.setView(here, 17, { animate: false });
        else map.fitBounds(b.extend(here), { padding: [30, 30], animate: false, maxZoom: 18 });
      } else if (follow && !map.getBounds().pad(-0.3).contains(here)) {
        map.setView(here, map.getZoom(), { animate: false });
      }

      if (staged !== null) {
        const to = L.latLng(staged.lat, staged.lon);
        L.polyline([here, to], {
          className: 'f3-teleop-leg',
          dashArray: '4 4',
          interactive: false,
        }).addTo(layer);
        L.circleMarker(to, {
          radius: 6,
          className: 'f3-teleop-waypoint',
          interactive: false,
        }).addTo(layer);
      }
    }
  }, [
    readings,
    poseLat,
    poseLon,
    poseHeading,
    robot.robot_id,
    frame.replay,
    frame.t,
    follow,
    staged,
    staleFix,
    poseAge,
    coverage,
    siblings,
    planned,
    on,
    zoomGen,
  ]);

  const fixes = readings.filter((r) => r.lat != null && r.lon != null).length;
  const hasFix = fixes > 0 && !staleFix && !frame.replay;
  const peers = siblings.filter((s) => s.lat != null).length;

  const LAYERS: readonly { key: LayerKey; label: string; n: number; title: string }[] = [
    { key: 'track', label: 'track', n: fixes, title: 'Telemetry fixes held by this console' },
    {
      key: 'coverage',
      label: 'capture coverage',
      n: coverage.length,
      title: 'Each capture session’s own reported bounding box (/api/robots/…/scans)',
    },
    {
      key: 'planned',
      label: 'planned path',
      n: planned.length,
      title: 'Waypoints carried by dispatched `move` commands, in issue order',
    },
    {
      key: 'fleet',
      label: 'fleet at site',
      n: peers,
      title: 'Other robots registered to this robot’s site, at their last known fix',
    },
  ];

  return (
    <Panel
      className={styles.mapPanel}
      viewport
      pad="none"
      title="Position"
      meta={
        <>
          <LiveTag
            live={hasFix}
            at={frame.replay ? frame.t : null}
            label="Fix"
            pulse={hasFix}
          />
          <span>{fixes} fixes</span>
          <span
            data-tone={canStage ? 'nominal' : 'neutral'}
            title={
              canStage
                ? 'Click the map to stage a move waypoint. Nothing is sent until you commit.'
                : stageBlocked === 'verb'
                  ? '`move` is not in this morphology’s command profile'
                  : 'This robot has never reported a position, so there is no origin to stage a move from.'
            }
          >
            {canStage
              ? 'click to stage move'
              : stageBlocked === 'verb'
                ? 'no move verb'
                : 'no fix to stage from'}
          </span>
        </>
      }
      tools={
        <>
          <IconButton
            icon="crosshair"
            label={follow ? 'Following the robot — click to release' : 'Follow the robot'}
            size="sm"
            active={follow}
            onClick={() => setFollow((v) => !v)}
          />
          <IconButton
            icon="expand"
            label="Frame the track and every capture footprint"
            size="sm"
            onClick={() => {
              framed.current = false;
              setOn((v) => ({ ...v }));
            }}
          />
          <IconButton
            icon="zoom-in"
            label="Zoom in"
            size="sm"
            onClick={() => mapRef.current?.zoomIn(1)}
          />
          <IconButton
            icon="zoom-out"
            label="Zoom out"
            size="sm"
            onClick={() => mapRef.current?.zoomOut(1)}
          />
        </>
      }
    >
      <div className={styles.mapBody}>
        <div className={styles.mapHost} ref={hostRef} />

        {/* The layer tree — user-ordered, numbered, per-node visibility, count
            on every row (palantir-gotham-map-workspace.png). Not a checkbox
            soup and not a legend: each row is the switch AND the key. */}
        <div className={styles.layers} role="group" aria-label="Map layers">
          {LAYERS.map((l, i) => (
            <button
              key={l.key}
              type="button"
              className={styles.layerRow}
              data-key={l.key}
              data-on={on[l.key]}
              aria-pressed={on[l.key]}
              title={l.title}
              onClick={() => setOn((v) => ({ ...v, [l.key]: !v[l.key] }))}
            >
              <span className={styles.layerNo}>{i + 1}</span>
              <span className={styles.layerSwatch} aria-hidden="true" />
              <span className={styles.layerName}>{l.label}</span>
              <span className={styles.layerCount} data-zero={l.n === 0}>
                {l.n}
              </span>
            </button>
          ))}
        </div>

        {pose === null && (
          <div className={styles.mapNoFix}>
            {robot.robot_id} ·{' '}
            {frame.replay
              ? `no fix at or before ${axisClock(frame.t)}`
              : 'no position fix on record'}
          </div>
        )}
        {frame.replay && (
          <button
            type="button"
            className={styles.mapReplay}
            onClick={onGoLive}
            title="This map is drawing a scrubbed instant, not the live pose. Return every panel to now (⇧L)."
          >
            <StatusDot tone="warn" hollow />
            REPLAY {axisClock(frame.t)}
            <span className={styles.mapReplayGo}>Go Live</span>
          </button>
        )}
        {/* Bottom-left is ambient truth (palantir-gotham-map-workspace.png's
            "Map health — Connected"); bottom-right is furniture. */}
        <div className={styles.mapFoot}>
          <span data-tone={tiles === 'unavailable' ? 'warn' : 'nominal'}>
            <StatusDot tone={tiles === 'unavailable' ? 'warn' : 'nominal'} hollow={tiles !== 'ok'} />
            {BASEMAP} · {tiles === 'ok' ? 'connected' : tiles === 'pending' ? 'connecting' : 'unavailable'}
          </span>
          <span className={styles.mapFootSep}>·</span>
          <span>
            {frame.replay
              ? `pose ${rShown?.ts === undefined ? NO_VALUE : axisClock(Date.parse(rShown.ts))}`
              : `last fix ${fixAgeS === null ? NO_VALUE : duration(fixAgeS * 1000)}`}
          </span>
          <span
            className={styles.frustumOff}
            title="THE BAR §11.8: the camera's ground footprint is a live claim about what the operator can see. With no stream and no gimbal pose it is removed from the map, not greyed. The capture rectangles are a different claim — a finished session's recorded extent."
          >
            <Icon name="video-off" size={12} />
            no frustum
          </span>
        </div>
        {pose !== null && (
          <div className={styles.mapFix}>{latlon(pose.lat, pose.lon)}</div>
        )}
        {children}
      </div>
    </Panel>
  );
}

/* ==========================================================================
   THE CONTROL RAIL
   ========================================================================== */

function LeasePanel({
  robot,
  media,
  lease,
  operator,
  authed,
  telemetryAgeS,
  onRequest,
}: {
  robot: Robot;
  media: MediaConfig | null;
  lease: Lease;
  operator: string;
  authed: boolean;
  telemetryAgeS: number | null;
  onRequest: () => void;
}) {
  const enabled = lease.state === 'observing' || lease.state === 'held';
  /* OPEN where there is room for it. The disclosure was closed on the
     argument that six inactive rungs are documentation and documentation on
     screen is chrome — which is true only while the space is contested. On a
     tall viewport it is not: with the safety bar pinned to the rail's foot,
     this block sat above 260px of measured void at 1920×1080, and THE BAR
     §11.6 lists seven states and says "a critic should ask to see each". A
     machine whose other six rungs are behind a chevron is a machine you have
     to have read the PRD to trust.
     Under ~980px of viewport the rail genuinely is contested — the coverage
     list is already the thing that scrolls — so the default flips back and
     the chevron is the way in. */
  const [ladder, setLadder] = useState(() => window.innerHeight >= 980);
  const step = LEASE_LADDER.find((s) => s.id === lease.state) ?? LEASE_LADDER[0]!;
  return (
    <Section
      label="Control lease"
      aside={<span className={styles.railAside}>exclusive · backend-arbitrated</span>}
    >
      {/* A SWITCH, not a button (THE BAR §11.6): a switch shows its state when
          nobody is looking at it, and the lock state has to be legible from
          2 m. It is not the surface's accent verb — that budget belongs to
          the safety bar. */}
      <button
        type="button"
        role="switch"
        aria-checked={false}
        aria-label="Take control of this robot"
        className={styles.lock}
        disabled={!enabled}
        onClick={onRequest}
        data-on={false}
      >
        <span className={styles.lockTrack} aria-hidden="true">
          <span className={styles.lockKnob}>
            <Icon name="lock" size={12} />
          </span>
        </span>
        <span className={styles.lockWord}>Locked</span>
        <span className={styles.lockKbd}>⇧U</span>
      </button>

      {/* The live rung, and the disclosure for the rest of the machine. */}
      <button
        type="button"
        className={styles.leaseNow}
        aria-expanded={ladder}
        onClick={() => setLadder((v) => !v)}
        title="The control-lease state machine (PRD-0002 §2 / D4) — seven states"
      >
        <StatusDot tone="warn" />
        <span className={styles.leaseNowLabel}>{step.label}</span>
        <span className={styles.leaseNowCond}>{lease.reason}</span>
        <Icon name={ladder ? 'chevron-down' : 'chevron-right'} size={12} />
      </button>

      {ladder && (
        <ol className={styles.ladder} aria-label="Control-lease state machine">
          {LEASE_LADDER.map((s) => {
            const current = s.id === lease.state;
            return (
              <li key={s.id} className={styles.ladderRow} data-current={current}>
                <StatusDot tone={current ? 'warn' : 'neutral'} hollow={!current} />
                <span className={styles.ladderLabel}>{s.label}</span>
                <span className={styles.ladderCond}>{s.condition}</span>
              </li>
            );
          })}
        </ol>
      )}

      {/* The holder row is printed only when there IS one. Three em dashes
          under a control that already says `no media plane` is the same fact
          three more times. */}
      {lease.holder !== null && (
        <div className={styles.holder}>
          <span>
            holder <b>{lease.holder}</b>
          </span>
          <span>
            for <b data-missing={lease.since === null}>{lease.since ?? NO_VALUE}</b>
          </span>
          <span className={styles.holderYou}>
            you <b>{operator}</b>
          </span>
        </div>
      )}

      <Preconditions
        robot={robot}
        media={media}
        operator={operator}
        authed={authed}
        telemetryAgeS={telemetryAgeS}
      />
    </Section>
  );
}

/**
 * The ground this robot has recorded, as the sessions that recorded it.
 *
 * This is the map's coverage layer given a key: one row per capture session,
 * in the same order and the same state vocabulary as the rectangles drawn on
 * the map, and clicking a row frames the map on that session's own reported
 * bbox. It exists because a stroke-only footprint with a corner label answers
 * "where" but not "what state, how long, how big" — and because those three
 * facts are real, server-reported, and were previously nowhere on this screen.
 */
function CoverageList({
  coverage,
  onFocus,
}: {
  coverage: readonly Coverage[];
  onFocus: (c: Coverage) => void;
}) {
  return (
    <Section
      label="Capture coverage"
      aside={<span className={styles.railAside}>on the map</span>}
    >
      {coverage.length === 0 ? (
        <div className={styles.covEmpty}>No capture sessions on record for this robot.</div>
      ) : (
        <ul className={styles.covList}>
          {coverage.map((c) => (
            <li key={c.id}>
              <button
                type="button"
                className={styles.covRow}
                data-state={c.state ?? 'raw'}
                onClick={() => onFocus(c)}
                title={`Frame the map on ${c.session} — scan ${c.id}, ${scanState(c.state).label}${
                  c.started === null ? '' : `, started ${utc(c.started)}`
                }`}
              >
                <StatusDot tone={scanState(c.state).tone} hollow={c.state !== 'copc'} />
                <span className={styles.covName}>{c.session}</span>
                <span className={styles.covState}>{scanState(c.state).label}</span>
                <span className={styles.covSize}>{bytes(c.size)}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </Section>
  );
}

/**
 * The drive surface. InOrbit's D-pad is grey and inert until the lease is
 * unlocked and only then rings orange; ours is the same idea with the axes
 * an operator actually commands — v (m/s) and ω (rad/s) — drawn as a field
 * rather than four arrows, because a rover's cmd_vel is continuous.
 *
 * It is INERT, and it says so. Direct manipulation must never dispatch
 * (tell 31), and with no lease there is nothing to dispatch onto: the
 * crosshair stays at the origin and both readouts are em dashes.
 */
function DrivePad({
  armed,
  measured,
  sample,
}: {
  armed: boolean;
  measured: number | null;
  sample: SampleAge;
}) {
  return (
    <div className={styles.pad} data-armed={armed}>
      <svg viewBox="0 0 120 120" className={styles.padField} role="img"
           aria-label="Drive input field — inert, no control lease. Rings mark 0.5 and 1.0 of full deflection.">
        <rect className={styles.padBox} x="0.5" y="0.5" width="119" height="119" />
        <line className={styles.padGrid} x1="60" y1="0" x2="60" y2="120" />
        <line className={styles.padGrid} x1="0" y1="60" x2="120" y2="60" />
        {/* The two rings are the INPUT's own normalised domain (0.5 and 1.0 of
            full deflection), not a claimed speed cap: the contract carries no
            per-robot velocity limit, and PRD-0002 §3 puts caps on the robot
            where they are enforced. Drawing a limit we do not have would be
            the same lie as drawing a latency we have not measured. */}
        <circle className={styles.padLimit} cx="60" cy="60" r="52" />
        <circle className={styles.padLimit} cx="60" cy="60" r="26" />
        <circle className={styles.padDot} cx="60" cy="60" r="3" />
        <path className={styles.padCross} d="M60,52 L60,68 M52,60 L68,60" />
      </svg>
      <div className={styles.padSide}>
        <div className={styles.padRead}>
          <span className={styles.padReadLabel}>v</span>
          <span className={styles.padReadValue} data-missing="true">{NO_VALUE}</span>
          <Unit unit="m/s" className={styles.padReadUnit} />
        </div>
        <div className={styles.padRead}>
          <span className={styles.padReadLabel}>ω</span>
          <span className={styles.padReadValue} data-missing="true">{NO_VALUE}</span>
          <Unit unit="rad/s" className={styles.padReadUnit} />
        </div>
        {/* v̂ is the only reading in this block that comes off the wire, so it
            is the only one that can go stale — and `0.00 m/s` on a robot that
            stopped reporting last night is the same lie as a live-looking
            vitals ring. It dims and carries its age, exactly as the strip
            does. */}
        <div className={styles.padRead} data-measured="true" data-stale={sample.stale}>
          <span className={styles.padReadLabel}>v̂</span>
          <span className={styles.padReadValue}>
            {measured == null ? NO_VALUE : measured.toFixed(2)}
          </span>
          <Unit unit="m/s" className={styles.padReadUnit} />
          {sample.stale && measured != null && (
            <span className={styles.padReadAge}>{sample.age}</span>
          )}
        </div>
        <div
          className={styles.padNote}
          title="v and ω are the commanded pair, normalised −1…1 with the velocity caps enforced robot-side (PRD-0002 §3). v̂ is speed over ground, measured, from telemetry speed_mps."
        >
          −1…1 · nothing is sent
        </div>
      </div>
    </div>
  );
}

const KEYMAP: readonly { keys: string; what: string }[] = [
  { keys: 'W S', what: 'drive' },
  { keys: 'A D', what: 'yaw' },
  { keys: 'Space', what: 'deadman' },
  { keys: 'E', what: 'E-STOP' },
  { keys: '⇧X', what: 'safe stop' },
  { keys: '⇧H', what: 'return home' },
];

function DriveBlock({
  armed,
  measuredMps,
  sample,
}: {
  armed: boolean;
  measuredMps: number | null;
  sample: SampleAge;
}) {
  return (
    <Section
      label="Drive"
      aside={
        <span className={styles.railAside} data-tone={armed ? 'active' : 'neutral'}>
          {armed ? 'armed' : 'inert'} · keyboard fallback
        </span>
      }
    >
      {/* THE GAMEPAD READOUT CAME OUT HERE. It polled `navigator.getGamepads()`
          on a 1 s interval and printed the pad's id, or "no gamepad detected".

          What it measured was the BROWSER's view of a USB device on the
          operator's desk — which is a true fact about this laptop and tells
          nobody anything about whether the robot can be driven. Nothing reads
          a gamepad yet: the control affordances are disabled, the deadman is
          `robot · not asserted`, and #294 is where a held input starts
          publishing cmd_vel. Until then a green dot beside a plugged-in pad
          suggested a readiness that does not exist, and a grey one suggested a
          missing part that is not the missing part. */}
      <div className={styles.deadman}>
        <span className={styles.deadmanWord} data-driving={false}>
          Stopped
        </span>
      </div>
      <DrivePad armed={armed} measured={measuredMps} sample={sample} />

      <ul
        className={styles.keymap}
        aria-label="Keyboard bindings"
        title="PRD-0002 D2 makes a physical held input the primary deadman; a key can be taped down, so the keyboard is a clearly-marked lower-assurance fallback."
      >
        {KEYMAP.map((k) => (
          <li key={k.keys} className={styles.keyRow}>
            <kbd className={styles.kbd}>{k.keys}</kbd>
            <span className={styles.keyWhat}>{k.what}</span>
          </li>
        ))}
      </ul>
    </Section>
  );
}

/**
 * The safety model, as four named interlocks rather than as prose.
 *
 * PRD-0002 §3 is explicit that all four are enforced ROBOT-SIDE and that the
 * console only assists, so the second column names the enforcing party and
 * the live state of the thing that would trip it — three of the four cannot
 * trip today because the channel they watch has never opened, and the dot is
 * hollow for exactly that reason (§11.5's three renderings: true, false, and
 * never-published are three different facts).
 *
 * The fourth is real: the console's E-STOP button works right now, because it
 * falls back to the command queue. It is also the only one whose latency this
 * console can quote, which is why the Control loop panel prints it.
 */
function InterlockBlock({
  streamConnected,
  hazardous,
}: {
  streamConnected: boolean;
  hazardous: readonly ProfileCommand[];
}) {
  const rows: { key: string; label: string; by: string; live: boolean; title: string }[] = [
    {
      key: 'deadman',
      label: 'deadman',
      by: 'robot · not asserted',
      live: false,
      title:
        'PRD-0002 design D2: a held physical input (gamepad/pedal) via the Gamepad API. Releasing it decelerates the robot to a stop. Nothing is asserting it — there is no session.',
    },
    {
      key: 'watchdog',
      label: 'latency watchdog',
      by: 'robot · no samples',
      live: false,
      title:
        'The robot tracks control-command freshness and safe-stops past its own threshold. Stale commands are never executed. The threshold is set robot-side and this console does not publish a number it cannot read.',
    },
    {
      key: 'link',
      label: 'link loss',
      by: 'robot · no channel',
      live: false,
      title:
        'Loss of the control channel makes the robot stop autonomously. A lost console never strands or runs away the robot.',
    },
    {
      key: 'estop',
      label: 'e-stop',
      by: streamConnected ? 'robot + console' : 'robot + console · queued',
      live: true,
      title: streamConnected
        ? 'PRD-0002 D10: e-stop rides the realtime channel and is robot-authoritative.'
        : 'The realtime channel is down here, so the console button falls back to the command queue and is NOT immediate. The robot-side e-stop is unaffected.',
    },
    ...(hazardous.length > 0
      ? []
      : [
          {
            key: 'tool',
            label: 'tool arming',
            by: 'none in profile',
            live: false,
            title:
              'Arming a `hazardous` actuator during teleop is a separate, interlocked action, never implied by holding drive control (PRD-0002 §3). This morphology declares none.',
          },
        ]),
  ];
  return (
    <Section
      label="Safety interlocks"
      aside={<span className={styles.railAside}>robot-side · authoritative</span>}
    >
      <ul className={styles.locks} aria-label="Safety interlocks">
        {rows.map((r) => (
          <li key={r.key} className={styles.lockRow} title={r.title}>
            <StatusDot tone={r.live ? 'ok' : 'neutral'} hollow={!r.live} />
            <span className={styles.lockName}>{r.label}</span>
            <span className={styles.lockBy} data-live={r.live}>{r.by}</span>
          </li>
        ))}
      </ul>
    </Section>
  );
}

/**
 * The hazardous actuator (PRD-0002 §3, PRD-0001 §5). Arming a `hazardous`
 * command during teleop is a separate, interlocked action that is NEVER
 * implied by holding drive control — the koko's chainsaw is the case this
 * exists for, and the profile is where the console learns whether this
 * particular robot has one at all.
 */
function ToolBlock({ hazardous }: { hazardous: readonly ProfileCommand[] }) {
  return (
    <Section
      label="Hazardous actuator"
      aside={<span className={styles.railAside}>interlocked</span>}
    >
      <div
        className={styles.toolRow}
        title="Arming a hazardous actuator is a separate, deliberate, interlocked action and is NEVER implied by holding drive control. Deadman lapse, watchdog trip and link loss all disarm it, backed by the hardware power-isolation interlock (PRD-0003)."
      >
        {/* Disarmed is the SAFE state, so it is not red. THE BAR §11.5: a
            boolean reading false is a fact, not an alarm, and red is reserved
            for fault and destruction. The loud rendering belongs to ARMED. */}
        <StateLabel tone="neutral" label="DISARMED" />
        {hazardous.map((h) => (
          <span key={h.command} className={styles.toolCmd}>
            {h.command}
          </span>
        ))}
        <span className={styles.toolSpacer} />
        <span className={styles.toolWhy}>lease not held</span>
        <Button variant="secondary" size="sm" icon="lock" disabled>
          Arm
        </Button>
      </div>
    </Section>
  );
}

/**
 * THE SAFETY BAR — pinned to the rail's bottom edge, present in every lease
 * state, never in an overflow, never scrolled away, never disabled because
 * the lease is not held (THE BAR §11.7 / tell 25).
 *
 * E-STOP is red and filled, and it is the one saturated fill on this surface.
 * THE BAR §11.7 says abort is never destructive-red — written about `Return`
 * and `Land Now`, which are RECOVERY verbs. An emergency stop is not a
 * recovery verb: ISO 13850 makes it red, PRD-0002 design D3 asks for "the
 * loud red in a calm console", and every operator on earth already knows what
 * a red mushroom does. Return-home and safe-stop, which are the recovery
 * pair, are outlined and neutral exactly as the reference has them.
 */
function SafetyBar({
  onEstop,
  onSafeStop,
  onReturnHome,
  pending,
  canReturn,
}: {
  onEstop: () => void;
  onSafeStop: () => void;
  onReturnHome: () => void;
  pending: boolean;
  canReturn: boolean;
}) {
  return (
    <div className={styles.safety} role="group" aria-label="Safety">
      <button
        type="button"
        className={styles.estop}
        onClick={onEstop}
        disabled={pending}
        title="Immediate stop. One press, no confirmation. The robot's own e-stop is authoritative (PRD-0002 §3)."
      >
        <Icon name="alert-octagon" size={16} />
        E-STOP
        <span className={styles.estopKbd}>E</span>
      </button>
      <div className={styles.recover}>
        <Button variant="secondary" size="sm" icon="stop" kbd="⇧X" onClick={onSafeStop} disabled={pending}>
          Safe stop
        </Button>
        <Button
          variant="secondary"
          size="sm"
          icon="home"
          kbd="⇧H"
          onClick={onReturnHome}
          disabled={pending || !canReturn}
          title={canReturn ? undefined : 'return_home is not in this morphology’s command profile'}
        >
          Return home
        </Button>
      </div>
      <div className={styles.safetyNote}>
        robot-side safe-stop is authoritative · console assists
      </div>
    </div>
  );
}

/* ==========================================================================
   THE LEDGER — queue + audit
   ========================================================================== */

const CMD_TONE: Record<CommandStatus, Tone> = {
  pending: 'neutral',
  sent: 'ok',
  acked: 'ok',
  completed: 'good',
  failed: 'error',
  cancelled: 'neutral',
};

const HAZARD_TONE: Record<string, Tone> = {
  benign: 'neutral',
  motion: 'warn',
  hazardous: 'error',
};

/**
 * The command ledger — one row per command, two lines, and the second line is
 * the audit trail that used to be a whole second table.
 *
 * Round 4 printed `stop_mapping 105` as a queue row AND as `issued 105` +
 * `sent 105` in a Session audit stacked underneath it: one dataset, two
 * tables, ~500px of the centre column. What the event table said that the
 * queue could not was per-command provenance — how many transitions this
 * command has been through, when the current one started, and who actually
 * moved it (the issuer signs the command; `system` and `auto-dispatch` move
 * it afterwards). All three are facts ABOUT A ROW, so they belong on the row,
 * as THE BAR §5's 40–44px two-line row with the dim second line. The table is
 * now denser than either of the two it replaces and repeats nothing.
 *
 * Read against the playhead, not the wall clock. Scrubbing to 13:39:11Z does
 * not filter the table — hiding rows would be a worse lie than showing them —
 * but every AGE is measured from the instant on screen, a command issued after
 * that instant reads `not yet` rather than a negative duration, and the trail
 * is CUT at the cursor so a transition that had not happened yet cannot be
 * printed as if it had. In replay `Issued` also drops its relative age for an
 * absolute Zulu stamp: a relative age is the one thing that keeps ticking
 * when the clock you are reading it against has stopped.
 */
interface Trail {
  /** Transitions at or before the cursor, newest first. */
  events: readonly AuditEvent[];
  /** The newest one — the transition that put the command in its state. */
  newest: AuditEvent | null;
}

function trailsOf(
  audit: readonly AuditEvent[] | undefined,
  at: number,
  replay: boolean,
): Map<number, Trail> {
  const out = new Map<number, Trail>();
  for (const e of audit ?? []) {
    if (replay && Date.parse(e.ts) > at) continue;
    const prev = out.get(e.command_id);
    const events = prev === undefined ? [e] : [...prev.events, e];
    out.set(e.command_id, { events, newest: null });
  }
  for (const [id, t] of out) {
    const sorted = [...t.events].sort((a, b) => Date.parse(b.ts) - Date.parse(a.ts));
    out.set(id, { events: sorted, newest: sorted[0] ?? null });
  }
  return out;
}

/** Two ISO stamps that land in the same wall-clock second. */
function sameSecond(a: string, b: string): boolean {
  return Math.abs(Date.parse(a) - Date.parse(b)) < 1000;
}

/* ---- the deck's silhouette, for a read that has not landed ---------------
   The same five regions the live deck draws, with the same column specs and
   the same lane labels, so a failed or in-flight read costs the operator
   values and never the surface. */
const LEASE_ROWS: readonly string[] = [
  'operator', 'robot link', 'media endpoint', 'lease', 'authority',
];
const TELEOP_LANES: readonly SkeletonLane[] = [
  { label: 'state' },
  { label: 'commands' },
  { label: 'teleop' },
  { label: 'speed', unit: 'm/s' },
];
const TELEOP_QUEUE_COLUMNS: readonly SkeletonColumn[] = [
  { label: 'State', width: '132px' },
  { label: 'Command' },
  { label: 'In state', width: '96px', align: 'right' },
  { label: 'Issued', width: '110px', align: 'right' },
  { label: 'By', width: '160px' },
];

function queueColumns(
  hazardOf: (name: string) => string,
  trails: Map<number, Trail>,
  now: number,
  replay: boolean,
): Column<Command>[] {
  return [
    {
      key: 'status',
      header: 'State',
      kind: 'status',
      cell: (c) => {
        const n = trails.get(c.id)?.events.length ?? 0;
        return (
          <>
            <span className={styles.qPrimary}>
              <StateLabel
                tone={CMD_TONE[c.status]}
                label={c.status}
                loud={c.status === 'failed'}
                title={`command ${c.id}`}
              />
            </span>
            {/* The id, because it is what the audit strip and the transport's
                command lane both print and the only handle an operator can
                carry to a log; then how many transitions it took to get here,
                which is the one shape fact the deleted event table had. */}
            <span className={styles.qSub} title={`command ${c.id} — ${n} audited transitions`}>
              #{c.id}
              <span className={styles.qDim}>{n === 0 ? ' no audit' : ` ${n} ev`}</span>
            </span>
          </>
        );
      },
    },
    {
      key: 'command',
      header: 'Command',
      kind: 'id',
      mono: true,
      cell: (c) => {
        const h = hazardOf(c.command);
        return (
          <>
            <span className={styles.qPrimary}>{c.command}</span>
            {/* The hazard was its own column in round 4 and repeated `benign`
                down eight rows. As a second line it costs no width and the
                one `motion` row still reads. */}
            <span className={styles.qSub} data-tone={HAZARD_TONE[h] ?? 'neutral'}>
              {h}
            </span>
          </>
        );
      },
    },
    {
      key: 'instate',
      header: 'In state',
      kind: 'age',
      align: 'right',
      cell: (c) => {
        const d = now - Date.parse(c.updated_at);
        const newest = trails.get(c.id)?.newest ?? null;
        return (
          <>
            <span className={styles.qPrimary} data-num="true">
              {d < 0 ? <span className={styles.future}>not yet</span> : duration(d)}
            </span>
            {/* The bare stamp, not `since 19:14:38Z`: the header already says
                In state, and the six characters the word costs are what push
                `By` — the only accountability column on the table — over the
                shed threshold at 1280.

                Suppressed entirely when the transition IS the issue, which is
                every command that has only ever been `pending`: printing
                15:33:26Z twice on one row is the duplication this merge exists
                to remove, at row scale. */}
            <span
              className={styles.qSub}
              data-num="true"
              data-missing={newest === null}
              title={
                newest === null
                  ? 'No audited transition for this command'
                  : `Entered ${c.status} at ${utc(newest.ts)}, per the audit trail`
              }
            >
              {newest === null
                ? NO_VALUE
                : sameSecond(newest.ts, c.issued_at)
                  ? ''
                  : utc(newest.ts).slice(11)}
            </span>
          </>
        );
      },
    },
    {
      key: 'issued',
      header: 'Issued',
      kind: 'age',
      align: 'right',
      cell: (c) => (
        <>
          <span className={styles.qPrimary} data-num="true">
            {utc(c.issued_at).slice(11)}
          </span>
          {/* The absolute stamp leads and the relative age is the second line,
              not the other way round: in replay a relative age is measured
              against a clock that has stopped, and the one that keeps ticking
              under a parked playhead is exactly §11.3's `Last pong` trap. */}
          <span className={styles.qSub} data-num="true">
            {replay ? (
              Date.parse(c.issued_at) > now ? (
                <span className={styles.future}>not yet</span>
              ) : (
                <span
                  className={styles.atCursor}
                  title={`${utc(c.issued_at)} — the playhead is at ${axisClock(now)}`}
                >
                  {duration(now - Date.parse(c.issued_at))} before cursor
                </span>
              )
            ) : (
              <Timestamp value={c.issued_at} />
            )}
          </span>
        </>
      ),
    },
    {
      key: 'by',
      header: 'By',
      kind: 'id',
      mono: true,
      drop: 1,
      cell: (c) => {
        /* The issuer signs the command; whoever moved it last is a different
           accountability question and the audit is the only place it exists.
           Printed only when the two differ — `tim@forest3.ai / tim@forest3.ai`
           is a row of noise. */
        const mover = trails.get(c.id)?.newest?.operator ?? null;
        return (
          <>
            <span className={styles.qPrimary}>{c.issued_by}</span>
            <span
              className={styles.qSub}
              title={mover === null ? undefined : `last moved into its current state by ${mover}`}
            >
              {mover === null || mover === c.issued_by ? '' : `→ ${mover}`}
            </span>
          </>
        );
      },
    },
  ];
}

/**
 * The session/audit strip — one line, at the foot of the queue it audits.
 *
 * This is what is LEFT of the audit once its duplicate half is gone. The
 * per-event table said `sent 105 · system · 4s ago` for events whose command
 * was already a row eight pixels above it; what it said that nothing else
 * did was the SHAPE of the trail — how many transitions this robot's queue
 * has emitted, over how many distinct actors, and which one was last. Three
 * facts, one line, and they are bound to the transport like everything else:
 * scrub back and `newest` becomes the newest event at or before the playhead,
 * not the newest that will ever exist.
 *
 * The right-hand clause is the honest one and it is the reason this strip is
 * called `session` and not `command`: PRD-0002 §5 puts lease grants, handoffs
 * and deadman trips in this same trail, and none of them have ever been
 * written, because no teleop session has ever opened. That sentence belongs
 * on the audit surface and nowhere else on the screen.
 */
function AuditStrip({
  audit,
  at,
  replay,
}: {
  audit: readonly AuditEvent[] | undefined;
  at: number;
  replay: boolean;
}) {
  const all = audit ?? [];
  const upto = replay ? all.filter((e) => Date.parse(e.ts) <= at) : all;
  const newest = upto.reduce<AuditEvent | null>(
    (best, e) => (best === null || Date.parse(e.ts) > Date.parse(best.ts) ? e : best),
    null,
  );
  const actors = new Set(upto.map((e) => e.operator)).size;

  return (
    <div className={styles.auditStrip}>
      <span className={styles.auditEyebrow}>Audit</span>
      {/* `ev` / `op`, not `events` / `actors`: the strip has to hold a full
          UTC stamp, a verb, a command id, an operator and the teleop clause
          inside a 460px ledger at 1280, and the two words were what pushed
          `#105` off the end. The abbreviation is the same one the State
          column uses two rows above (`#105 2 ev`), so it is a vocabulary this
          screen already teaches. */}
      <span className={styles.auditCount} title="Audited command transitions held by this page">
        {upto.length}
        <span className={styles.auditWord}>{replay ? ' ev to cursor' : ' ev'}</span>
      </span>
      <span
        className={styles.auditCount}
        title="Distinct operators and processes that appear in that trail"
      >
        {actors}
        <span className={styles.auditWord}> op</span>
      </span>
      <span className={styles.auditRule} aria-hidden="true" />
      {newest === null ? (
        <span className={styles.auditNone}>
          {replay ? 'no events at or before the playhead' : 'no audited events for this robot'}
        </span>
      ) : (
        <span
          className={styles.auditNewest}
          title={`Newest audited transition${replay ? ' at or before the playhead' : ''} — command ${newest.command_id}`}
        >
          <span className={styles.auditTs}>{utc(newest.ts).slice(11)}</span>
          <StateLabel
            tone={CMD_TONE[newest.event as CommandStatus] ?? 'accent'}
            label={newest.event}
            loud={newest.event === 'failed'}
          />
          <span className={styles.auditCmd}>#{newest.command_id}</span>
          <span className={styles.auditOp}>{newest.operator}</span>
        </span>
      )}
      <span className={styles.auditSpacer} />
      <span
        className={styles.auditPending}
        title="PRD-0002 §5 writes lease grants, handoffs, deadman trips and safe-stops into this same trail. None exist: no teleop session has ever opened on this platform."
      >
        no teleop yet
      </span>
    </div>
  );
}

/* ==========================================================================
   THE TRANSPORT — the spine of the screen, not a chart at the bottom of it.

   THE BAR §5 calls the shared time axis the spine of the app; §11 makes it
   non-negotiable on a teleop screen; tell 10 asks for a `Go Live` escape and
   tell 21 for a band that shows where data actually exists. All three want
   the same object, and it is not a plot with a hover tooltip. It is a
   TRANSPORT: a draggable playhead, a rate, an explicit return to live, and
   four other panels that follow it.

   So this panel owns an instant and everything else on the screen reads it.
   Drag the playhead to 13:39:11Z and the HUD tapes, the vitals rings, the
   map pose, the map's track head and the two ledgers all move to 13:39:11Z,
   and every one of those panels flips its own liveness chip from `Live` to
   `Live off 13:39:11` — per panel, per §5's InOrbit rule, never one global
   badge. With no media plane this is the only way the screen ever shows a
   *picture* of a session, and it is an honest one: it is made entirely of
   readings the robot actually sent.

   Three honesty rules do the real work:

   · A hole in the band is a hole in the DATA
     (formant-timeline-data-presence-scrubber.png). The presence band under
     the lanes is painted from the readings themselves, so a coverage gap is
     visible BEFORE you scrub into it — and when you scrub into one, every
     bound instrument says `no data at cursor` rather than holding up the
     last value it happened to have.
   · The console fetches the newest 240 readings, not all of history, so
     everything left of the oldest one it holds is `beyond fetch window` —
     NOT `no data`. Claiming the robot was silent because this page did not
     ask is the same class of lie as an invented latency (tell 21).
   · The teleop lane exists and is empty, because no session has ever been
     opened on any robot on this platform.
   ========================================================================== */

const HOUR_MS = 3_600_000;

const WINDOWS = [
  { value: '1h', label: '1 h', ms: HOUR_MS },
  { value: '6h', label: '6 h', ms: 6 * HOUR_MS },
  { value: '12h', label: '12 h', ms: 12 * HOUR_MS },
  { value: '24h', label: '24 h', ms: 24 * HOUR_MS },
] as const;

type WindowKey = (typeof WINDOWS)[number]['value'];

/* The rate control THE BAR §5 requires beside `Go Live`, at the rates this
   data actually has. Telemetry lands every ~2m 24s on this fleet, so a 1×
   replay of a six-hour window is six hours of nothing moving: 1× is offered
   because a transport that cannot do real time is lying about being one,
   and 60×/600× are the rates at which a shift is reviewable. */
const RATES = [
  { value: '1', label: '1×', x: 1 },
  { value: '60', label: '60×', x: 60 },
  { value: '600', label: '600×', x: 600 },
] as const;

type RateKey = (typeof RATES)[number]['value'];

function rateOf(key: RateKey): number {
  return (RATES.find((r) => r.value === key) ?? RATES[1]).x;
}

interface Sample {
  t: number;
  state: string | null;
  speed: number | null;
  battery: number | null;
  cpu: number | null;
  heading: number | null;
}

function stateTone(state: string | null): Tone {
  if (state === null) return 'neutral';
  const d = STATUS_STATE[state as RobotStatus];
  return d === undefined ? 'neutral' : d.tone;
}

/** One cell of the data-presence band. Three states, three renderings. */
type Presence = 'data' | 'gap' | 'unfetched';

function SessionTimeline({
  robotId,
  readings,
  commands,
  cadenceMs,
  hazardOf,
  frame,
  now,
  onScrub,
  onGoLive,
  playing,
  onPlaying,
  rate,
  onRate,
}: {
  robotId: string;
  readings: readonly TelemetryReading[];
  commands: readonly Command[];
  cadenceMs: number | null;
  hazardOf: (name: string) => string;
  /** The instant the whole screen is showing — this panel sets it. */
  frame: Frame;
  now: number;
  onScrub: (t: number) => void;
  onGoLive: () => void;
  playing: boolean;
  onPlaying: (next: boolean) => void;
  rate: RateKey;
  onRate: (next: RateKey) => void;
}) {
  /* 6 h is the default because it is the shortest window in which this
     platform's real dispatch bars are still wider than a hairline: a
     90-second command over 12 h is 2 px, and the lane correctly answers that
     with "(zoom for more)" rather than by inflating the bar. Duration is
     never padded to make a mark visible — the window moves instead. */
  const [winKey, setWinKey] = useState<WindowKey>('6h');
  const spanMs = (WINDOWS.find((w) => w.value === winKey) ?? WINDOWS[1]).ms;
  /* The window edge is quantised to 30 s so the whole band does not shuffle
     sideways once a second; the playhead itself still advances live. */
  const to = Math.ceil(now / 30_000) * 30_000;
  const from = to - spanMs;
  const cad = cadenceMs !== null && cadenceMs > 0 ? cadenceMs : 60_000;

  const trackRef = useRef<HTMLDivElement | null>(null);
  const [w, setW] = useState(0);
  const [hover, setHover] = useState<number | null>(null);

  const measure = useCallback((el: HTMLDivElement | null) => {
    trackRef.current = el;
    if (el) setW(el.getBoundingClientRect().width);
  }, []);

  useEffect(() => {
    const el = trackRef.current;
    if (el === null) return;
    const ro = new ResizeObserver((entries) => {
      const r = entries[0]?.contentRect;
      if (r) setW(r.width);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const model = useMemo(() => {
    const samples: Sample[] = readings
      .map((r) => ({
        t: Date.parse(r.ts),
        state: r.state,
        speed: r.speed_mps,
        battery: r.battery_pct,
        cpu: r.cpu_pct,
        heading: r.heading_deg,
      }))
      .filter((r) => Number.isFinite(r.t))
      .sort((a, b) => a.t - b.t);

    /* ---- lane 1: robot state, with real gaps ---------------------------- */
    const gapMs = Math.max(cad * 2.5, 30_000);
    const state: RibbonSegment[] = [];
    const push = (start: number, end: number, label: string, tone: Tone, hollow: boolean) => {
      if (end > from && start < to && end > start) {
        state.push({ start, end, label, tone, hollow });
      }
    };

    if (samples.length === 0) {
      push(from, to, 'no telemetry on record', 'neutral', true);
    } else {
      const first = samples[0]!;
      const last = samples[samples.length - 1]!;
      if (first.t > from) push(from, first.t, 'beyond fetch window', 'neutral', true);

      let segStart = first.t;
      let segState = first.state;
      let prev = first.t;
      for (let i = 1; i < samples.length; i += 1) {
        const r = samples[i]!;
        if (r.t - prev > gapMs) {
          push(segStart, prev + cad, segState ?? 'unknown', stateTone(segState), segState === null);
          push(prev + cad, r.t, 'no data', 'neutral', true);
          segStart = r.t;
          segState = r.state;
        } else if (r.state !== segState) {
          push(segStart, r.t, segState ?? 'unknown', stateTone(segState), segState === null);
          segStart = r.t;
          segState = r.state;
        }
        prev = r.t;
      }
      push(segStart, last.t + cad, segState ?? 'unknown', stateTone(segState), segState === null);
      if (last.t + cad < to) push(last.t + cad, to, 'no data', 'neutral', true);
    }

    /* ---- lane 2: the command queue, as duration bars at true times -------
       Painted on its own presence backing: the queue this console holds runs
       back only as far as the oldest command it fetched, and "nothing was
       dispatched here" and "this page never asked" are different facts. */
    const cmd: RibbonSegment[] = [];
    let oldestCmd = Infinity;
    for (const c of commands) {
      const t0 = Date.parse(c.issued_at);
      if (Number.isFinite(t0)) oldestCmd = Math.min(oldestCmd, t0);
    }
    if (commands.length === 0) {
      cmd.push({ start: from, end: to, label: 'no commands on record', tone: 'neutral', hollow: true });
    } else {
      if (oldestCmd > from) {
        cmd.push({ start: from, end: Math.min(to, oldestCmd), label: 'beyond fetch window', tone: 'neutral', hollow: true });
      }
      if (oldestCmd < to) {
        cmd.push({ start: Math.max(from, oldestCmd), end: to, label: 'queue idle', tone: 'neutral', hollow: true });
      }
    }
    for (const c of commands) {
      const t0 = Date.parse(c.issued_at);
      const t1 = Date.parse(c.updated_at);
      if (!Number.isFinite(t0)) continue;
      const end = Number.isFinite(t1) && t1 > t0 ? t1 : t0 + 1000;
      if (end <= from || t0 >= to) continue;
      cmd.push({
        start: t0,
        end,
        label: c.command,
        tone: CMD_TONE[c.status],
        hollow: c.status === 'pending' || c.status === 'cancelled',
      });
    }

    /* ---- the bound trace, resampled onto the window's own uniform grid ---
       Plot positions a sample by INDEX, so an irregular time array would put
       the trace out of register with the ribbons above it. Binning onto a
       fixed grid keeps index ∝ time exactly, and an empty bin stays null so a
       coverage hole breaks the line in the same place the band shows it. */
    const bins = Math.max(20, Math.min(300, Math.floor(spanMs / (cad * 1.5))));
    const step = spanMs / Math.max(1, bins - 1);
    const times: number[] = [];
    const speed: (number | null)[] = [];
    for (let i = 0; i < bins; i += 1) times.push(from + i * step);
    const sum = new Array<number>(bins).fill(0);
    const n = new Array<number>(bins).fill(0);
    for (const r of samples) {
      if (r.t < from || r.t > to || r.speed == null) continue;
      const i = Math.max(0, Math.min(bins - 1, Math.round((r.t - from) / step)));
      sum[i] = (sum[i] ?? 0) + r.speed;
      n[i] = (n[i] ?? 0) + 1;
    }
    for (let i = 0; i < bins; i += 1) speed.push((n[i] ?? 0) === 0 ? null : (sum[i] as number) / (n[i] as number));

    const peak = samples.reduce((m, r) => (r.speed != null && r.speed > m ? r.speed : m), 0);
    const ceiling = Math.max(0.5, Math.ceil(peak / 0.25) * 0.25);

    /* ---- the data-presence band ----------------------------------------
       formant-timeline-data-presence-scrubber.png's blue band with white
       gaps, at our geometry. The cell is never narrower than one and a half
       sampling intervals — a band bucketed finer than the robot reports
       would print a gap between every pair of perfectly good readings, which
       is the same lie in the other direction. */
    const cellMs = Math.max(spanMs / 260, cad * 1.5);
    const cells = Math.max(1, Math.ceil(spanMs / cellMs));
    const oldestHeld = samples.length === 0 ? Infinity : samples[0]!.t;
    const band: Presence[] = new Array<Presence>(cells).fill('gap');
    for (let i = 0; i < cells; i += 1) {
      if (from + (i + 1) * cellMs <= oldestHeld) band[i] = 'unfetched';
    }
    for (const r of samples) {
      if (r.t < from || r.t > to) continue;
      const i = Math.max(0, Math.min(cells - 1, Math.floor((r.t - from) / cellMs)));
      band[i] = 'data';
    }

    const inWindow = samples.filter((r) => r.t >= from && r.t <= to);
    const marks = commands
      .map((c) => ({ t: Date.parse(c.issued_at), status: c.status, name: c.command }))
      .filter((m) => Number.isFinite(m.t) && m.t >= from && m.t <= to);

    return { state, cmd, times, speed, ceiling, samples, inWindow, band, cells, cellMs, marks };
  }, [readings, commands, from, to, cad, spanMs]);

  const lanes: RibbonLane[] = [
    { key: 'state', label: 'state', segments: model.state },
    { key: 'cmd', label: 'commands', segments: model.cmd },
    {
      key: 'teleop',
      label: 'teleop',
      segments: [
        { start: from, end: to, tone: 'neutral', label: 'no teleop sessions', hollow: true },
      ],
    },
  ];

  /* ---- the transport's own readout -----------------------------------
     THE BAR §5: "a playhead line plus a two-column aligned tooltip listing
     every series' value at that instant… this is what makes a plot an
     instrument rather than a picture." It was a hover tooltip, which meant
     it was clipped by its own panel and gone the moment the pointer left.
     Here it is a fixed column of the panel: it cannot clip, it survives the
     pointer leaving, and it reads the PLAYHEAD — the same instant the map
     and the stage are showing, so the four surfaces are one instrument. */
  const cursorT = frame.t;
  const cursorF = frac(cursorT, from, to);

  /* ---- and the crosshair REPORTS -------------------------------------
     A crosshair that draws but does not report is strictly worse than no
     crosshair. The pointer inspects without committing: the same column
     retargets to the hovered instant, in a THIRD header state distinct from
     both LIVE and REPLAY, and snaps back to the playhead when the pointer
     leaves. Inspecting is not scrubbing — nothing else on the screen moves,
     because the bound panels follow the playhead and the playhead has not
     gone anywhere.

     The retarget fires on exactly the condition that draws the line, so the
     two can never disagree: within 8px of the playhead there is no second
     line and there is no second reading. Clamped at `now`, because there is
     no instant after now to inspect. */
  const hoverT = hover === null ? null : Math.min(hover, now);
  const hoverF = hoverT === null ? null : frac(hoverT, from, to);
  const probing = hoverT !== null && hoverF !== null && Math.abs(hoverF - cursorF) * w > 8;
  const readT = probing && hoverT !== null ? hoverT : cursorT;
  /* `replay: true` on a probe is deliberate: an inspected instant obeys the
     coverage rule (shownReading), so hovering into a hole prints em dashes
     rather than holding up the last value before it. */
  const readFrame: Frame = probing ? frameAt(readings, readT, true, cadenceMs) : frame;
  const mode: 'live' | 'replay' | 'hover' = probing ? 'hover' : frame.replay ? 'replay' : 'live';

  const laneAt = (lane: RibbonLane): RibbonSegment | null => {
    let hit: RibbonSegment | null = null;
    for (const s of lane.segments) if (readT >= s.start && readT < s.end) hit = s;
    return hit;
  };
  /* The SAME resolution rule the stage and the map use — shownReading(), not
     a second opinion. A readout that says `no data at cursor` while the
     vitals strip two panels up is showing four numbers is exactly the class
     of disagreement this whole frame object exists to make impossible. */
  const r = shownReading(readFrame);
  const readoutRows: {
    key: string;
    label: string;
    value: string;
    unit?: string;
    tone?: Tone;
    missing?: boolean;
  }[] = [
    ...lanes.map((l) => {
      const seg = laneAt(l);
      return {
        key: l.key,
        label: l.label,
        value: seg?.label ?? NO_VALUE,
        ...(seg === null || seg.hollow === true ? { missing: true } : { tone: seg.tone }),
      };
    }),
    {
      key: 'v',
      label: 'speed',
      value: r?.speed_mps == null ? NO_VALUE : r.speed_mps.toFixed(2),
      unit: 'm/s',
      missing: r?.speed_mps == null,
    },
    {
      key: 'hdg',
      label: 'heading',
      value: r?.heading_deg == null ? NO_VALUE : `${r.heading_deg.toFixed(0)}°`,
      unit: 'N',
      missing: r?.heading_deg == null,
    },
    /* Battery and not CPU: the transport's readout carries the quantities
       that have a HISTORY on this axis and a consequence for the drive
       decision. CPU is a ring on the vitals strip, at the instant this
       readout is already naming, and printing it twice would not make it
       any truer. */
    {
      key: 'bat',
      label: 'battery',
      value: r?.battery_pct == null ? NO_VALUE : r.battery_pct.toFixed(0),
      unit: '%',
      missing: r?.battery_pct == null,
    },
    {
      key: 'age',
      label: 'sample',
      value: r === undefined ? 'no data at cursor' : readFrame.sample.age,
      ...(r === undefined
        ? { missing: true }
        : readFrame.sample.stale
          ? { tone: 'warn' as Tone }
          : {}),
    },
  ];
  /* The hazard class of whatever the queue was doing at this instant — the
     one fact on this readout that is about consequence rather than about
     magnitude, so it goes last and it is toned. */
  const cmdAt = laneAt(lanes[1] as RibbonLane);
  if (cmdAt !== null && cmdAt.hollow !== true) {
    const h = hazardOf(cmdAt.label);
    readoutRows.push({ key: 'hz', label: 'hazard', value: h, tone: HAZARD_TONE[h] ?? 'neutral' });
  }

  /* ---- interaction ---------------------------------------------------- */
  const tAt = (clientX: number): number => {
    const el = trackRef.current;
    if (el === null) return cursorT;
    const box = el.getBoundingClientRect();
    const f = Math.max(0, Math.min(1, (clientX - box.left) / Math.max(1, box.width)));
    return Math.round(from + f * (to - from));
  };

  const scrubTo = (t: number) => {
    onPlaying(false);
    onScrub(Math.min(t, now));
  };

  const onDown = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (e.button !== 0) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    scrubTo(tAt(e.clientX));
  };

  const onMove = (e: ReactPointerEvent<HTMLDivElement>) => {
    const t = tAt(e.clientX);
    setHover(t);
    if ((e.buttons & 1) === 1) onScrub(Math.min(t, now));
  };

  const onKey = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    /* Stepping from the keyboard hands the readout back to the playhead. A
       pointer parked on the rail must not keep the column pinned to an
       instant the operator has just stepped away from. */
    setHover(null);
    const stepMs = cad * (e.shiftKey ? 10 : 1);
    if (e.key === 'ArrowLeft' || e.key === ',') {
      e.preventDefault();
      scrubTo(cursorT - stepMs);
    } else if (e.key === 'ArrowRight' || e.key === '.') {
      e.preventDefault();
      scrubTo(Math.min(now, cursorT + stepMs));
    } else if (e.key === 'Home') {
      e.preventDefault();
      scrubTo(from);
    } else if (e.key === 'End') {
      e.preventDefault();
      onGoLive();
    } else if (e.key === ' ') {
      e.preventDefault();
      if (frame.replay) {
        onPlaying(!playing);
      } else {
        /* Space on a live transport is "show me the run up to here": drop the
           playhead twenty samples back and play forward into now. */
        onScrub(Math.max(from, cursorT - cad * 20));
        onPlaying(true);
      }
    }
  };

  const oldest = model.samples[0];
  const cellW = (model.cellMs / (to - from)) * 100;

  return (
    <Panel
      className={styles.fit}
      title="Session timeline"
      meta={
        <>
          {/* Per-panel liveness, and it has to be EARNED (THE BAR §5 —
              `((•)) Live` ⇄ `((x)) Live off`). It flips the instant the
              operator drags the playhead off now, and again when the newest
              sample crosses the same staleness threshold the stage and the
              map use. */}
          <LiveTag
            live={!frame.replay && !frame.sample.stale && model.inWindow.length > 0}
            at={frame.replay ? cursorT : null}
            label="Live"
            pulse={!frame.replay && !frame.sample.stale}
          />
          <span title="Every lane, the trace and the four bound panels read this window">
            {model.inWindow.length} readings · {model.marks.length} commands
          </span>
        </>
      }
      tools={
        <>
          <IconButton
            icon={playing ? 'pause' : 'play'}
            label={
              playing
                ? 'Pause replay'
                : frame.replay
                  ? `Play from ${axisClock(cursorT)} at ${rate}×`
                  : 'Scrub off live first — the playhead is at now'
            }
            size="sm"
            disabled={!frame.replay}
            active={playing}
            onClick={() => onPlaying(!playing)}
          />
          <SegmentedControl
            items={RATES.map((x) => ({ value: x.value, label: x.label, disabled: !frame.replay }))}
            value={rate}
            onChange={onRate}
            label="Replay rate"
          />
          <button
            type="button"
            className={styles.goLive}
            disabled={!frame.replay}
            onClick={onGoLive}
            title="Return the playhead to now and re-bind every panel to the live stream (⇧L)"
          >
            <StatusDot tone={frame.replay ? 'warn' : 'nominal'} hollow={frame.replay} />
            Go Live
            <span className={styles.goLiveKbd}>⇧L</span>
          </button>
          <SegmentedControl
            items={WINDOWS.map((x) => ({ value: x.value, label: x.label }))}
            value={winKey}
            onChange={setWinKey}
            label="Timeline window"
          />
        </>
      }
      pad="none"
      scroll={false}
    >
      <div className={styles.tlBody}>
        <div className={styles.tlGutter} aria-hidden="true">
          {lanes.map((l) => (
            <div key={l.key} className={styles.tlLaneLabel}>
              {l.label}
            </div>
          ))}
          <div className={styles.tlPlotLabel}>speed</div>
          <div className={styles.tlBandLabel}>data</div>
        </div>

        {/* The whole stack is the scrub target — lanes, trace and band — the
            way every reference transport behaves. Pointer capture means a
            drag that wanders off the panel keeps driving the playhead. */}
        <div
          className={styles.tlTrack}
          ref={measure}
          role="slider"
          tabIndex={0}
          aria-label={`Session playhead — ${robotId}. Drag to scrub; End returns to live.`}
          aria-valuemin={from}
          aria-valuemax={to}
          aria-valuenow={cursorT}
          aria-valuetext={frame.replay ? `replay ${axisClock(cursorT)}` : `live ${axisClock(cursorT)}`}
          onPointerDown={onDown}
          onPointerMove={onMove}
          onPointerLeave={() => setHover(null)}
          onKeyDown={onKey}
        >
          {lanes.map((l) => (
            <Ribbon key={l.key} segments={l.segments} from={from} to={to} label={l.label} width={w} height={13} />
          ))}
          <div className={styles.tlPlot}>
            <Plot
              times={model.times}
              series={[
                { key: 'speed', label: 'speed_mps', values: model.speed, tone: 'nominal', unit: 'm/s', digits: 2 },
              ]}
              height={44}
              min={0}
              max={model.ceiling}
              unit="m/s"
              cursor={cursorT}
              readout={false}
            />
          </div>

          {/* The data-presence band: solid where this robot actually
              reported, open where it did not, hatched where this page never
              asked. It is also the scrub rail, so the operator's hand is on
              the coverage while they choose an instant. */}
          <div
            className={styles.tlBand}
            title={`Data presence, one cell per ${duration(model.cellMs)} — solid where a reading exists, open where none does, hatched before the oldest reading this console fetched. A cell is never narrower than one and a half sampling intervals: a finer band would print a gap between two perfectly good readings.`}
          >
            {model.band.map((p, i) => (
              <span
                key={i}
                className={styles.tlCell}
                data-p={p}
                style={{ left: `${i * cellW}%`, width: `${cellW}%` }}
              />
            ))}
            {model.marks.map((m) => (
              <span
                key={`${m.t}-${m.name}`}
                className={styles.tlMark}
                data-tone={CMD_TONE[m.status]}
                style={{ left: `${frac(m.t, from, to) * 100}%` }}
                title={`${m.name} · ${m.status} · ${axisClock(m.t)}`}
              />
            ))}
          </div>

          <TimeAxis from={from} to={to} now={now} />

          {/* Where a click would land, and what is there. One hairline and
              nothing else on the rail: the instant and every series' value at
              it are answered by the readout column, which is retargeted by
              the same `probing` flag that draws this line — so the crosshair
              can never draw without reporting. It disappears within 8px of
              the playhead so a drag never draws two lines on top of each
              other, and the column falls back to the playhead in the same
              breath. */}
          {probing && hoverF !== null && (
            <span className={styles.tlHover} style={{ left: `${hoverF * 100}%` }} />
          )}
          <span
            className={styles.tlPlayhead}
            data-replay={frame.replay}
            style={{ left: `${cursorF * 100}%` }}
          >
            <span className={styles.tlHandle} aria-hidden="true" />
          </span>
        </div>

        {/* The two-column readout, promoted out of a tooltip. Three header
            states: LIVE at now, REPLAY on a committed playhead, HOVER while
            the pointer is inspecting an instant it has not committed to. */}
        <div
          className={styles.tlReadout}
          data-mode={mode}
          title={
            mode === 'hover'
              ? 'Inspecting — these are the values at the crosshair, not at the playhead. Click to move the playhead here.'
              : mode === 'replay'
                ? 'The instant every bound panel on this screen is showing.'
                : 'Now. Every bound panel on this screen is reading the live stream.'
          }
        >
          <div className={styles.tlReadoutHead}>
            <span className={styles.tlReadoutClock}>{axisClock(readT)}</span>
            <span className={styles.tlReadoutMode}>{mode}</span>
          </div>
          <dl className={styles.tlRows}>
            {readoutRows.map((row) => (
              <div key={row.key} className={styles.tlRow}>
                <dt>{row.label}</dt>
                <dd
                  data-tone={row.tone ?? 'neutral'}
                  data-missing={row.missing === true}
                  title={`${row.label} ${row.value}${row.unit === undefined ? '' : `${unitIsTight(row.unit) ? '' : ' '}${row.unit}`}`}
                >
                  {row.value}
                  {row.unit !== undefined && !(row.missing === true) && (
                    <Unit unit={row.unit} className={styles.tlUnit} />
                  )}
                </dd>
              </div>
            ))}
          </dl>
        </div>

        {/* Four facts, not six: the window is already the lit segment in the
            header and the presence cell is the band's own tooltip, and a
            footer that ellipsises every fact it carries has told you none of
            them. Each survivor carries a title, so a narrow panel still
            answers on hover. */}
        <div className={styles.tlFoot}>
          <span title="Every lane, the trace and the presence band are drawn against the reading's own timestamp, in UTC.">
            axis · reading ts (UTC)
          </span>
          <span className={styles.tlFootSep}>·</span>
          <span title="The interval this robot actually reports at, measured over the newest 20 samples.">
            cadence {cadenceMs === null ? NO_VALUE : duration(cadenceMs)}
          </span>
          {/* Below ~1500 these two drop out whole rather than every fact
              ellipsising: three truncated facts are worse than two entire
              ones, and both of these are recoverable — the fetch window from
              the presence band's own hatching, the endpoint from the panel's
              own title attribute. */}
          <span className={`${styles.tlFootSep} ${styles.tlFootWide}`}>·</span>
          <span
            className={styles.tlFootWide}
            title="The console holds the newest 240 readings; anything older was never fetched, and the presence band says so rather than claiming silence."
          >
            held {model.samples.length} · from {oldest === undefined ? NO_VALUE : axisClock(oldest.t)}
          </span>
          <span className={`${styles.tlFootSep} ${styles.tlFootWide}`}>·</span>
          <span
            className={styles.tlFootWide}
            title="The endpoint every lane on this transport is drawn from."
          >
            /api/robots/{robotId}/telemetry
          </span>
          <span className={styles.tlFootSpacer} />
          {/* THE BAR §6: a shortcut is printed in the affordance it drives.
              These are live on the scrub rail once it has focus, which is why
              they are printed on the rail's own footer and not in a help
              dialog nobody opens. */}
          <span className={styles.tlKeys}>
            <kbd>drag</kbd> scrub
            <kbd>space</kbd> play
            <kbd>, .</kbd> step
            <kbd>⇧L</kbd> live
          </span>
        </div>
      </div>
    </Panel>
  );
}

/* ==========================================================================
   THE SURFACE
   ========================================================================== */

export function Teleop() {
  const { robotId, navigate } = useFleetRoute();
  const media = useMemo(mediaConfig, []);
  const now = useNow();

  const robotQ = useRobot(robotId);
  const { data: robot } = robotQ;
  // The live subscription (#244). It opens nothing until start() is called, so
  // merely visiting this screen never makes a robot publish — video is on
  // demand, and an unwatched robot spends no CPU and no bytes on it (D15).
  // Telemetry first: the camera list is derived from its payload (C5/D7).
  const { data: telemetry } = useTelemetry(robotId, { limit: 240 });

  const cameras: readonly ServableCamera[] = useMemo(() => {
    for (const r of telemetry ?? []) {
      const raw = (r.payload as { video_topics?: unknown } | null)?.video_topics;
      if (!Array.isArray(raw)) continue;
      const topics = raw
        .map((t) => t as { topic?: unknown; default?: unknown })
        .filter((t) => typeof t?.topic === 'string' && (t.topic as string).length > 0)
        .map((t) => ({ topic: t.topic as string, isDefault: t.default === true }));
      if (topics.length > 0) return topics;
    }
    return [];
  }, [telemetry]);

  /* WHICH CAMERA THE SESSION ASKS FOR.
     `null` means "not chosen yet" and resolves to the robot's own default — RGB
     — rather than to `cameras[0]`, which is sorted and would be the DEPTH
     camera on this robot. The robot marks its default in the advertised list
     because only it knows which of rgb / depth / an overlay an operator means
     by "the camera", and a heuristic on the topic name breaks on the first
     morphology that names things differently. */
  const [pickedCamera, setPickedCamera] = useState<string | null>(null);
  const [camAnchor, setCamAnchor] = useState<{ x: number; y: number } | null>(null);
  const defaultCamera = useMemo(
    () => cameras.find((c) => c.isDefault)?.topic ?? null, [cameras]);
  const activeCamera = pickedCamera ?? defaultCamera;

  /* A choice that is no longer on the robot's list must not persist: the camera
     may have gone away, and asking for it would be refused on the robot. */
  useEffect(() => {
    if (pickedCamera !== null && !cameras.some((c) => c.topic === pickedCamera)) {
      setPickedCamera(null);
    }
  }, [cameras, pickedCamera]);

  /* Close on any click or key elsewhere, like the device menu. Without this the
     picker stays open behind whatever the operator does next, over a live
     picture. */
  useEffect(() => {
    if (camAnchor === null) return;
    const close = () => setCamAnchor(null);
    window.addEventListener('pointerdown', close);
    window.addEventListener('keydown', close);
    return () => {
      window.removeEventListener('pointerdown', close);
      window.removeEventListener('keydown', close);
    };
  }, [camAnchor]);

  // The session asks for the chosen camera; changing it re-opens the session,
  // which is what `cameraTopic` in the hook's effect deps is for.
  /* D11. Held here rather than in the hook because it describes the session an
     operator is ABOUT to open, not the one running: flipping it while live
     would re-open a stream that colleagues are watching, since it sits in the
     hook's effect deps beside `cameraTopic`. The control is disabled while a
     session runs for the same reason. */
  const [recordNext, setRecordNext] = useState(false);
  const [railCollapsed, setRailCollapsed] = useState(readRailCollapsed);
  /* The stage-over-the-page layer. It lives here rather than in the stage
     because the DECK is the page, and the page is what the layer is measured
     against — the shell's header and side bar are outside it and stay
     untouched, which is the whole point of scoping it this way. */
  const [stageExpanded, setStageExpanded] = useState(false);
  const video = useVideoSession(robot?.robot_id, {
    ...(activeCamera ? { cameraTopic: activeCamera } : {}),
    ...(recordNext ? { record: true } : {}),
  });
  /* One read, one vocabulary — a 404 is "this robot is not on record" and gets
     the way out; anything else is "we could not read it" and keeps the retry.
     `RobotDetail` reads the same endpoint the same way. */
  const robotRead = useRead(robotQ, {
    path: robotId === undefined ? undefined : `/api/robots/${robotId}`,
    everyMs: 5_000,
    subject: robotId ?? 'this robot',
  });

  /* Camera inventory comes from the ROBOT, on its telemetry payload (C5/D7,
     #245) — not from a LiveKit session, which PRD-0010 superseded, and not from
     the registry, which does not know what a topic costs to serve.
     `fleet_client.video_topics` publishes only what this robot will actually
     serve: live publishers, within D9's CPU budget, and nothing on its deny
     list. So an empty picker now means the robot said nothing rather than that
     nobody asked, and offering a camera the robot would refuse is not possible
     from here.

     Read from the newest reading that HAS the key, not simply the newest
     reading. The list is refreshed on a slow clock while telemetry arrives
     every few seconds, so the most recent reading frequently predates the most
     recent enumeration — taking `[0]` blindly would blink the picker empty
     between refreshes. */
  // `cameras` / `activeCamera` are derived above, beside the other hooks.
  const { data: commands } = useRobotCommands(robotId);
  const { data: audit } = useCommandAudit(robotId);
  const { data: revisions } = useRobotRevisions(robotId);
  const { data: scans } = useRobotScans(robotId);
  const { data: fleet } = useRobots();
  const { data: auth } = useAuth();
  const { connected: streamConnected } = useCommandStream(robotId);
  const issue = useIssueCommand();

  const [linkOpen, setLinkOpen] = useState(false);
  /* THE TRANSPORT'S STATE, and it belongs here rather than in the timeline
     panel, because it is not the timeline's state — it is the whole screen's.
     `scrub === null` means live; anything else is the instant every bound
     panel is showing. */
  const [scrub, setScrub] = useState<number | null>(null);
  const [playing, setPlaying] = useState(false);
  const [rate, setRate] = useState<RateKey>('60');
  const [focus, setFocus] = useState<Coverage | null>(null);
  const [staged, setStaged] = useState<Staged | null>(null);
  /* Bumped only when the MAP sets the point, so the numeric inputs re-seed on
     a click but never re-format the digits under the operator's cursor. */
  const [stagedGen, setStagedGen] = useState(0);
  const [notice, setNotice] = useState<{ tone: 'neutral' | 'warn' | 'error'; text: string } | null>(
    null,
  );

  const readings = telemetry ?? [];
  const latest = readings[0];
  const rtt = useMemo(() => dispatchRtt(audit), [audit]);
  /* Derived once and shared: the rail lists these sessions and the map draws
     them, and the two must never disagree about how many there are. */
  const coverage = useMemo(() => coverageOf(scans), [scans]);

  const telemetryAgeS =
    latest === undefined ? null : Math.max(0, (now - Date.parse(latest.ts)) / 1000);
  const fixAgeS = (() => {
    const fix = readings.find((r) => r.lat != null && r.lon != null);
    return fix === undefined ? null : Math.max(0, (now - Date.parse(fix.ts)) / 1000);
  })();

  /* The robot's own reporting interval, measured over the newest 20 samples.
     Everything that has to decide "is this reading close enough to that
     instant to speak for it" reads this one number. */
  const cadenceMs = (() => {
    if (readings.length < 3) return null;
    const a = Date.parse(readings[0]!.ts);
    const b = Date.parse(readings[Math.min(readings.length - 1, 20)]!.ts);
    const n = Math.min(readings.length - 1, 20);
    return n > 0 && Number.isFinite(a) && Number.isFinite(b) ? (a - b) / n : null;
  })();

  /* THE FRAME. One instant, resolved once, handed to the stage, the vitals
     strip, the drive readout, the map and both ledgers. */
  const frame = frameAt(readings, scrub ?? now, scrub !== null, cadenceMs);

  /* The audit, indexed by command and CUT at the playhead, so the ledger's
     second lines cannot print a transition that had not happened yet at the
     instant on screen. */
  const trails = useMemo(
    () => trailsOf(audit, frame.t, frame.replay),
    [audit, frame.t, frame.replay],
  );

  /* The queue row the transport is parked on, when the operator got there by
     clicking one. Highlighting it closes the loop: the table drove the
     playhead, so the table has to show which row did it. */
  const cursorCmd = useMemo(
    () =>
      scrub === null
        ? null
        : ((commands ?? []).find((c) => Date.parse(c.issued_at) === scrub) ?? null),
    [scrub, commands],
  );

  const goLive = useCallback(() => {
    setScrub(null);
    setPlaying(false);
  }, []);

  /* Replay. THE BAR §7 permits exactly one kind of continuous motion in an
     ops console — "the playhead advancing in real time" — and this is it.
     Nothing else on the screen animates: the bound values SWAP as the
     playhead crosses each sample, which is what makes the motion legible as
     data rather than as decoration. Reaching now hands control back to the
     live stream rather than stopping in the recent past. */
  const live = scrub === null;
  useEffect(() => {
    if (!playing || live) return;
    const x = rateOf(rate);
    let raf = 0;
    let last = performance.now();
    /* 20 Hz, not 60. The playhead is the one thing on this screen allowed to
       move continuously (§7) and 50ms is well under the threshold at which it
       reads as smooth — while a 60 Hz playhead would re-render five bound
       panels and rebuild the map's layer group sixty times a second for no
       visible gain. */
    const STEP_MS = 50;
    const tick = (ts: number) => {
      const dt = ts - last;
      if (dt >= STEP_MS) {
        last = ts;
        setScrub((prev) => {
          if (prev === null) return null;
          const next = prev + dt * x;
          return next >= Date.now() ? null : next;
        });
      }
      raf = window.requestAnimationFrame(tick);
    };
    raf = window.requestAnimationFrame(tick);
    return () => window.cancelAnimationFrame(raf);
  }, [playing, rate, live]);

  /* Falling off the end of the replay is a return to live, not a pause on the
     last frame — so the transport's own button has to stop looking pressed. */
  useEffect(() => {
    if (live && playing) setPlaying(false);
  }, [live, playing]);

  /* A different robot is a different session. Never carry a playhead across. */
  useEffect(() => {
    setScrub(null);
    setPlaying(false);
  }, [robotId]);

  const send = useCallback(
    (command: CommandName, label: string, extra?: string) => {
      if (robotId === undefined) return;
      issue.mutate(
        { robotId, command },
        {
          onSuccess: (c) =>
            setNotice({
              tone: extra === undefined ? 'neutral' : 'warn',
              text: `${label} queued as command ${c.id}.${extra === undefined ? '' : ` ${extra}`}`,
            }),
          onError: (e) => setNotice({ tone: 'error', text: `${label} failed — ${String(e)}` }),
        },
      );
    },
    [issue, robotId],
  );

  const onEstop = useCallback(() => {
    /* PRD-0002 D10: e-stop rides the REALTIME channel and is robot-
       authoritative — it must never be a queued, polled command. When that
       channel is down the console must not silently pretend otherwise: it
       says so, loudly, and falls back to the queue while naming the fallback
       as slower. A silent enqueue here would be the worst bug in the app. */
    send(
      'stop',
      'E-STOP',
      streamConnected
        ? 'Robot-side e-stop is authoritative.'
        : 'REALTIME CHANNEL DOWN — this went to the command queue and is NOT immediate.',
    );
  }, [send, streamConnected]);

  const onSafeStop = useCallback(() => send('stop', 'Safe stop'), [send]);

  /* The commit. Direct manipulation staged it; only this sends it, and the
     staged geometry is cleared the instant the write is accepted so the map
     never keeps drawing a claim the queue has already taken over. */
  const onSendMove = useCallback(() => {
    if (robotId === undefined || staged === null) return;
    issue.mutate(
      { robotId, command: 'move', params: { waypoint: { lat: staged.lat, lon: staged.lon } } },
      {
        onSuccess: (c) => {
          setStaged(null);
          setNotice({
            tone: 'neutral',
            text:
              `Move queued as command ${c.id} — ${staged.lat.toFixed(5)}, ${staged.lon.toFixed(5)}. `
              + 'Watch the queue for sent → acked → completed.',
          });
        },
        onError: (e) => setNotice({ tone: 'error', text: `Move rejected — ${String(e)}` }),
      },
    );
  }, [issue, robotId, staged]);
  const onReturnHome = useCallback(() => send('return_home', 'Return home'), [send]);

  const onMenu = useCallback(
    (id: MenuAction) => {
      if (robot === undefined) return;
      if (id === 'detail') navigate('detail', { robotId: robot.robot_id });
      else if (id === 'copy-id') void navigator.clipboard?.writeText(robot.robot_id);
      else if (id === 'copy-fix') void navigator.clipboard?.writeText(latlon(robot.lat, robot.lon));
      else if (id === 'safe-stop') onSafeStop();
      // Video is operator-initiated and never implicit: opening this screen
      // must not make a robot publish (D15). `stop-video` removes THIS operator
      // from the session; the stream ends only when the last watcher goes
      // (D17), which is deliberately not what safe-stop does — an e-stop leaves
      // video running, because an emergency is when you most want to see.
      else if (id === 'start-video') video.start();
      else if (id === 'stop-video') video.stop();
    },
    [robot, navigate, onSafeStop, video],
  );

  /* THE BAR §6: shortcuts are printed in the affordance, and every one of
     these is printed somewhere on this screen. `E` is bare because an
     emergency stop must be one key — an accidental press stops a robot, which
     is the safe direction to fail. `L` and ⌘K belong to the shell. */
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const t = e.target;
      if (t instanceof HTMLElement && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA')) return;
      if (e.key === 'Escape') {
        setStaged(null);
        return;
      }
      if (!e.shiftKey && (e.key === 'e' || e.key === 'E')) {
        e.preventDefault();
        onEstop();
        return;
      }
      if (!e.shiftKey) return;
      switch (e.key) {
        case 'X':
          e.preventDefault();
          onSafeStop();
          break;
        case 'H':
          e.preventDefault();
          onReturnHome();
          break;
        case 'K':
          e.preventDefault();
          setLinkOpen((v) => !v);
          break;
        /* THE BAR §6 binds `L` to go-live; the app shell already owns bare L
           for its own poll toggle, so the surface takes the shifted one and
           prints it on the button. */
        case 'L':
          e.preventDefault();
          goLive();
          break;
        case 'D':
          if (robotId !== undefined) {
            e.preventDefault();
            navigate('detail', { robotId });
          }
          break;
        /* Video is operator-initiated and never implicit (D15). These are
           printed on the stage menu, and THE BAR §6 means a printed shortcut
           has to actually work — a label for a binding that does nothing is
           the same defect class as an instrument showing a number it did not
           measure. */
        case 'V':
          e.preventDefault();
          video.start();
          break;
        case 'B':
          e.preventDefault();
          video.stop();
          break;
        default:
          break;
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onEstop, onSafeStop, onReturnHome, navigate, robotId, goLive, video]);

  if (robotId === undefined) {
    return (
      <SurfaceShell
        eyebrow={<BackLink label="Fleet" onClick={() => navigate('list')} />}
        title="Teleoperation"
      >
        <div className={styles.bare}>
          <Panel title="Teleoperation" meta={<span>no robot in the path</span>}>
            <Empty
              prompt
              detail="/fleet/teleop/{robot_id}"
              action={
                <Button variant="secondary" size="sm" onClick={() => navigate('list')}>
                  Robot list
                </Button>
              }
            >
              A teleoperation console is opened against one robot.
            </Empty>
          </Panel>
        </div>
      </SurfaceShell>
    );
  }

  /* THE DECK'S OWN SHAPE, WITH NOTHING IN IT.

     These two branches used to be one 96px panel over 592px of bare ground,
     and the failing one printed `No robot with id jetrover-01 in the registry
     — /api/robots/jetrover-01 returned 404.` for ANY failure. Against a dead
     network that sentence is the console inventing a fact: the robot is on the
     roster, this browser could not read it. `useRead` tells the two apart —
     RobotDetail already does exactly this, and this is the same page's other
     surface. The layout below is the real deck: rail, two panes, transport,
     ledger, in their real proportions, so a failed read costs the operator
     values and never the surface. */
  if (robot === undefined) {
    return (
      <SurfaceShell
        eyebrow={<BackLink label="Fleet" onClick={() => navigate('list')} />}
        title={robotId}
        pad="none"
      >
        <div className={styles.deck}>
          <aside className={styles.rail} aria-label="Teleoperation controls">
            <div className={styles.railScroll}>
              <ReadPanel
                title="Control lease"
                read={robotRead}
                skeleton={<SkeletonRail labels={LEASE_ROWS} what="this robot" />}
                leave={{ label: 'Robot list', onSelect: () => navigate('list') }}
              >
                {null}
              </ReadPanel>
            </div>
          </aside>
          <div className={styles.stageCol}>
            <Panel className={styles.stagePanel} title="Camera" pad="none">
              <StoppedContext.Provider value={{ mode: 'stopped' }}>
                <SkeletonPlot lanes={2} ticks={4} what="a video stream" />
              </StoppedContext.Provider>
            </Panel>
          </div>
          <div className={styles.mapCol}>
            <Panel className={styles.mapPanel} title="Position" pad="none">
              <StoppedContext.Provider value={{ mode: 'stopped' }}>
                <SkeletonPlot lanes={2} ticks={4} what="a position" />
              </StoppedContext.Provider>
            </Panel>
          </div>
          <div className={styles.timeline}>
            <Panel title="Session timeline" pad="none">
              <StoppedContext.Provider value={{ mode: 'stopped' }}>
                <SkeletonPlot lanes={TELEOP_LANES} ticks={6} what="the session" />
              </StoppedContext.Provider>
            </Panel>
          </div>
          <div className={styles.ledger}>
            <Panel title="Command queue" pad="none">
              <StoppedContext.Provider value={{ mode: 'stopped' }}>
                <SkeletonTable columns={TELEOP_QUEUE_COLUMNS} rows={5} edge what="commands" />
              </StoppedContext.Provider>
            </Panel>
          </div>
        </div>
      </SurfaceShell>
    );
  }

  const lease = resolveLease(robot, media);
  const operator = auth?.user?.email ?? 'unauthenticated';
  const profile = readProfile(robot);
  const hazardous = profile.filter((p) => p.hazard === 'hazardous');
  const hazardOf = (name: string) => profile.find((p) => p.command === name)?.hazard ?? 'benign';
  const canReturn = profile.some((p) => p.command === 'return_home');
  const hasMoveVerb = profile.some((p) => p.command === 'move');
  const canMove = hasMoveVerb && robot.lat != null;
  const stageBlocked: 'verb' | 'fix' | null = canMove ? null : hasMoveVerb ? 'fix' : 'verb';
  const pinned = revisions?.revisions.find((r) => r.id === revisions.current) ?? null;
  const board =
    pinned === null || typeof pinned.meta['board'] !== 'string'
      ? null
      : (pinned.meta['board'] as string);
  const kind = metaStr(robot, 'kind');
  const site = metaStr(robot, 'site');
  const bay = metaStr(robot, 'bay');
  /* Everything else registered to this ground, at its last known fix. Real
     registry rows — an operator driving here needs to know who else is on it. */
  const siblings = (fleet ?? []).filter(
    (r) => r.robot_id !== robot.robot_id && site !== null && metaStr(r, 'site') === site,
  );
  const planned = plannedOf(commands ?? []);
  // `cameras` is computed with the other hooks, above the early returns.

  const provenance: { key: string; text: string; strong?: boolean }[] = [
    { key: 'model', text: robot.model },
    {
      key: 'rev',
      text: pinned === null ? robot.morphology : `${robot.morphology} ${pinned.version}`,
      strong: true,
    },
    ...(board === null ? [] : [{ key: 'board', text: board }]),
    { key: 'fw', text: robot.firmware === null ? 'no firmware' : `fw ${robot.firmware}` },
    ...(kind === null ? [] : [{ key: 'kind', text: kind }]),
    ...(site === null ? [] : [{ key: 'site', text: bay === null ? site : `${site}/${bay}` }]),
  ];

  return (
    <SurfaceShell
      eyebrow={
        <BackLink
          label={`Fleet · ${robot.robot_id}`}
          onClick={() => navigate('detail', { robotId: robot.robot_id })}
        />
      }
      title={robot.name ?? robot.robot_id}
      /* THE SAME IDENTITY ROW AS `/fleet/detail`, THROUGH THE SAME COMPONENT.
         This surface used to print it twice — once in the page frame's head
         and once again 33px below in a 26px band of its own, because the shell
         was rendering a workspace strip on this route by accident and the
         frame's head collapses under one. Both are gone: the strip because
         teleop is an object's surface, the band because `SurfaceShell` already
         has the slot, and the deck is 26px taller for it. */
      subtitle={
        <>
          <span className={styles.idTag}>{robot.robot_id}</span>
          <StatusBadge status={robot.status} />
          <LivenessBadge liveness={robot.liveness} pulse={robot.liveness === 'live'} />
          <span className={styles.provenance}>
            {provenance.map((p, i) => (
              <span key={p.key} className={styles.provItem} data-strong={p.strong === true}>
                {i > 0 && <span className={styles.provSep} aria-hidden="true">·</span>}
                {p.text}
              </span>
            ))}
          </span>
        </>
      }
      actions={
        <>
          <span className={styles.role} title="Your authority on this session">
            {operator}
            <span className={styles.roleWord}>observer</span>
          </span>
          <Button
            variant="ghost"
            size="sm"
            icon="external-link"
            kbd="⇧D"
            onClick={() => navigate('detail', { robotId: robot.robot_id })}
          >
            Robot detail
          </Button>
        </>
      }
      pad="none"
    >
      {/* ONE deck, in every state — THE BAR §11.1's triple pane at 18/41/41,
          with the transport and the ledger under it. It does not reshape
          itself when a track arrives, because a control surface that moves
          under the operator is a control surface they cannot hit without
          looking (§7, §11.7). */}
      <div className={styles.deck} data-stage-expanded={stageExpanded} data-rail={railCollapsed ? 'collapsed' : 'expanded'}>
        <aside className={styles.rail} aria-label="Teleoperation controls">
          {/* The affordances collapse; the e-stop below and the stage footer's
              facts do not (#299). */}
          <button
            type="button"
            className={styles.railToggle}
            onClick={() => {
              const next = !railCollapsed;
              setRailCollapsed(next);
              writeRailCollapsed(next);
            }}
            aria-expanded={!railCollapsed}
            title={railCollapsed
              ? 'Show the control panel'
              : 'Hide the control panel and give the width to the picture. The e-stop stays.'}
          >
            <Icon name={railCollapsed ? 'chevron-right' : 'chevron-left'} size={12} />
            <span>{railCollapsed ? '' : 'Controls'}</span>
          </button>
          <div className={styles.railScroll}>
            <LeasePanel
              robot={robot}
              media={media}
              lease={lease}
              operator={operator}
              authed={auth?.user != null}
              telemetryAgeS={telemetryAgeS}
              onRequest={() => undefined}
            />
            {/* Reading order is the operator's order of questions: may I drive,
                how do I drive, what stops it, is the tool locked — and only
                then the wiring. On a short viewport the rail scrolls, and this
                order puts Channels (whose transports the Control loop panel
                also carries) at the bottom where a clip costs least. */}
            <DriveBlock
              armed={false}
              measuredMps={shownReading(frame)?.speed_mps ?? null}
              sample={frame.sample}
            />
            <InterlockBlock streamConnected={streamConnected} hazardous={hazardous} />
            {hazardous.length > 0 && <ToolBlock hazardous={hazardous} />}
            <CoverageList
              coverage={coverage}
              onFocus={(c) => setFocus((prev) => (prev?.id === c.id ? { ...c } : c))}
            />
          </div>
          <SafetyBar
            onEstop={onEstop}
            onSafeStop={onSafeStop}
            onReturnHome={onReturnHome}
            pending={issue.isPending}
            canReturn={canReturn}
          />
        </aside>

        <div className={styles.stageCol}>
          {notice !== null && (
            <div className={styles.notice}>
              <InlineNotice tone={notice.tone} onDismiss={() => setNotice(null)}>
                {notice.text}
              </InlineNotice>
            </div>
          )}
          <VideoStage
            robot={robot}
            media={media}
            video={video}
            cameras={cameras}
            track={activeCamera}
            camAnchor={camAnchor}
            onToggleCameras={(r) => setCamAnchor(camAnchor ? null : r)}
            onPickCamera={(t) => { setPickedCamera(t); setCamAnchor(null); }}
            record={recordNext}
            onToggleRecord={() => setRecordNext((on) => !on)}
            frame={frame}
            expanded={stageExpanded}
            onExpandedChange={setStageExpanded}
            linkOpen={linkOpen}
            onOpenLink={() => setLinkOpen((v) => !v)}
            onAction={onMenu}
          />
          {/* The link drill-down (THE BAR §11.3). It hangs off the PING chip
              it belongs to, over the map, and costs the page nothing shut. */}
          {linkOpen && (
            <LinkHealth
              rtt={rtt}
              latest={latest}
              cadenceMs={cadenceMs}
              onClose={() => setLinkOpen(false)}
            />
          )}
        </div>

        <div className={styles.mapCol}>
          <TeleopMap
            robot={robot}
            readings={readings}
            fixAgeS={fixAgeS}
            frame={frame}
            coverage={coverage}
            focus={focus}
            siblings={siblings}
            planned={planned}
            staged={staged}
            canStage={canMove}
            stageBlocked={stageBlocked}
            onStage={(next) => {
              setStaged(next);
              setStagedGen((g) => g + 1);
            }}
            onGoLive={goLive}
          >
            {staged !== null && (
              <MoveInspector
                key={stagedGen}
                robot={robot}
                staged={staged}
                hazard={hazardOf('move')}
                pending={issue.isPending}
                onChange={setStaged}
                onCancel={() => setStaged(null)}
                onSend={onSendMove}
              />
            )}
          </TeleopMap>
        </div>

        <div className={styles.timeline}>
          <SessionTimeline
            robotId={robot.robot_id}
            readings={readings}
            commands={commands ?? []}
            cadenceMs={cadenceMs}
            hazardOf={hazardOf}
            frame={frame}
            now={now}
            onScrub={setScrub}
            onGoLive={goLive}
            playing={playing}
            onPlaying={setPlaying}
            rate={rate}
            onRate={setRate}
          />
        </div>

        {/* ONE ledger, not two.

            Round 4 stacked a Command queue over a Session audit and gave them
            the whole centre column. They are the same eight commands: the
            audit is the queue's transition log, so `stop_mapping 105` was
            printed as a row in the queue AND as `sent 105` and `issued 105` in
            the audit, and on koko-04 three commands filled two panels with six
            rows and 220px of void. A screen that prints one dataset three
            times (the commands lane on the transport is the third) is not
            dense, it is repetitive — and the ~230px it cost came straight out
            of the transport, which is the spine.

            So: the queue is the table, because it is the only one of the three
            that carries LIVE state (`pending 3h 18m` is an operational fact
            the event log cannot state). The audit keeps its unique content —
            how many events, over how many operators, and the newest one — as
            the one-line strip THE BRIEF asks for, pinned to the panel's foot.
            The chronological event stream itself is on the transport's
            `commands` lane at its true timestamps, which is where §5 says
            events belong. Nothing was lost; two copies were. */}
        <div className={styles.ledger}>
          <Panel
            className={styles.fit}
            title="Command queue"
            count={commands?.length ?? 0}
            meta={
              <>
                <LiveTag
                  live={!frame.replay}
                  at={frame.replay ? frame.t : null}
                  label={streamConnected ? 'Push' : 'Polling'}
                  pulse={streamConnected && !frame.replay}
                />
                <span title="Measured issue → first transition, over the audit trail">
                  ack {rtt.medianMs === null ? NO_VALUE : duration(rtt.medianMs)} med · n={rtt.n}
                </span>
              </>
            }
            pad="none"
            scroll={false}
          >
            <div className={styles.ledgerBody}>
              <div className={styles.ledgerTable}>
                <DataTable
                  density="two"
                  rows={commands ?? []}
                  columns={queueColumns(hazardOf, trails, frame.t, frame.replay)}
                  rowKey={(c) => String(c.id)}
                  currentKey={
                    scrub === null || cursorCmd === null ? null : String(cursorCmd.id)
                  }
                  onRowClick={(c) => {
                    setPlaying(false);
                    setScrub(Date.parse(c.issued_at));
                  }}
                  rowTone={(c) =>
                    frame.replay && Date.parse(c.issued_at) > frame.t ? null : CMD_TONE[c.status]
                  }
                  empty="No commands dispatched to this robot."
                />
              </div>
              <AuditStrip audit={audit} at={frame.t} replay={frame.replay} />
            </div>
          </Panel>
        </div>
      </div>
    </SurfaceShell>
  );
}
